import eventemitter from 'eventemitter3';

export default class RoboNationCore extends eventemitter
{
    //=================================================================
    // CONSTANTS
    //=================================================================
    //The maximum number of characters of a saved game slot
    static get SAVE_NAME_MAX_LENGTH() { return 100; }
    //The maximum number of save slots available
    static get MAX_SLOT_COUNT() { return 10; }
    //The maximum number of Players/AIs inside a game
    static get MAX_PLAYER_COUNT() { return 6; }
    //The identifier of the Ultimate robot
    static get ULTIMATE_ROBOT_ID() { return 16; }
    //=================================================================

    //=================================================================
    // EVENTS
    //=================================================================
    //-----------------------------------------------------------------
    //GAME INIT EVENTS
    //-----------------------------------------------------------------
    //Triggered after calling the init() function
    static get EVENT_READY() { return 'ready'; }
    //-----------------------------------------------------------------
    //GAME LOOP EVENTS
    //-----------------------------------------------------------------
    //Triggered before and after each game loop. The delta time is passed as a parameter
    static get EVENT_PRE_LOOP() { return 'preloop'; }
    static get EVENT_POST_LOOP() { return 'postloop'; }
    //Triggered when the current date changed and a new day has started
    static get EVENT_DAY_CHANGE() { return 'daychange'; }
    //Triggered when the current date changed and a new month has started
    static get EVENT_MONTH_CHANGE() { return 'monthchange'; }
    //-----------------------------------------------------------------
    //EVENT TRIGGERING EVENTS
    //-----------------------------------------------------------------
    //Triggered when a new event occurs. The event data object is passed as a parameter (see doc of the getCurrentEvent function). 
    //Be warned to check the affectedPlayers to see if the current player is affected by the event
    static get EVENT_CURRENT_EVENT_START() { return 'newevent'; }
    //Triggered when the current ongoing event has changed during its execution. The event data object is passed as a parameter (see doc of the getCurrentEvent function)
    //Be warned to check the affectedPlayers to see if the current player is affected by the event
    static get EVENT_CURRENT_EVENT_UPDATE() { return 'currenteventupdate'; }
    //Triggered when the current ongoing event has ended. The previously held event is passed as a data object (see doc of the getCurrentEvent function)
    static get EVENT_CURRENT_EVENT_END() { return 'currenteventchange'; }
    //-----------------------------------------------------------------
    //GAME STATE EVENTS
    //-----------------------------------------------------------------
    //Triggered when the game has been won by a player. The identifier of the winner is passed as a parameter
    static get EVENT_GAME_END() { return 'gameend'; }
    //-----------------------------------------------------------------
    //MISC EVENTS
    //-----------------------------------------------------------------
    //Triggered after receiving a debug log line from the core. The log message is passed as a parameter
    static get EVENT_DEBUG_LOG() { return 'debug-log'; }
    //=================================================================

    //=================================================================
    // ENUMS
    //=================================================================
    static get AI_DIFFICULTY() { return { RANDOM: 0, EASY: 1, MEDIUM: 2, HARD: 3 }; }
    static get GAME_SPEED() { return { STOPPED: 0, FAST: 1, NORMAL: 2, SLOW: 3 }; }
    static get MODIFIER_TYPE() { return { NOTHING : 0, DEMAND : 1, MATERIAL_PRICE : 2, ROBOT_PRICE : 3, RESEARCH_MONEY_COST : 4, RESEARCH_TIME : 5, ROBOT_PRODUCTION_TIME : 6, ROBOT_PRODUCTION_MONEY_COST : 7, ROBOT_PRODUCTION_MATERIAL_COST : 8, LOST_SPACE : 9, ADDED_STORAGE : 10, LOADING_QUEUE_TIME : 11, RESHUFFLE_TIME : 12, EXPECTED_SALARY : 13, CURRENT_SALARY : 14, QUANTITY_PER_CONTRACT : 15, QUALITY_PER_CONTRACT : 16, CAN_BUILD_ROBOT : 17, ADDED_PRODUCTION_LINE : 18, ADDED_PARALLEL_CONTRACT : 19, STAFF_BONUS_WAREHOUSE : 20, STAFF_BONUS_PRODUCTION : 21, STAFF_BONUS_RESEARCH : 22, STAFF_BONUS_SALES : 23, STAFF_BONUS_HUMAN_RESOURCES : 24, STAFF_COUNT : 25, APPLICANT_STATS : 26, STAFF_STATS_GAIN : 27 }; }
    static get NATION() { return { RANDOM: 0, NORTH: 1, EAST: 2, SOUTH: 3, WEST: 4, CENTRAL: 5 }; }
    static get RESEARCH_TREE() { return { RANDOM: 0, ROBOT: 1, TECHNOLOGY: 2, STAFF: 3 }; }
    static get AGREEMENT_CONDITION() { return { VALUATION: 1, ROBOT_SOLD: 2, AGREEMENT_CLEARED: 3, RESEARCH_LEVEL: 4 }; }
    static get DEPARTMENT() { return { WAREHOUSE: 1, ASSEMBLY: 2, RESEARCH: 3, HUMAN_RESOURCES: 4, SALES: 5, MARKET_STUDY: 6 }; }
    static get LOAN_TYPE() { return { FREE_CREDIT: 0, SMALL: 1, MEDIUM: 2, LARGE: 3 }; }
    static get PENALTY_TYPE() { return { NONE: 0, BROKEN_SUPPLIER_CONTRACT: 1, FAILED_SUPPLIER_CONTRACT: 2, BROKEN_AGREEMENT: 3, FAILED_AGREEMENT: 4, BANK_LOAN_SMALL: 5, BANK_LOAN_MEDIUM: 6, BANK_LOAN_LARGE: 7 }; }
    static get WIN_CONDITION() { return { NONE: 0, COMPANY_VALUATION: 1, ROBOT_PRODUCTION: 2, NATIONS_CLEARED: 3 }; }
    static get NOTIFICATION_GROUP() { return { GENERAL: 0, WAREHOUSE: 1, ASSEMBLY: 2, RESEARCH: 3, HUMAN_RESOURCES: 4, SALES: 5, EVENTS: 6, MARKET: 7, BANK: 8 }; }
    static get OUTCOME() { return { NEGATIVE: 0, POSITIVE: 1 }; }
    static get ASSEMBLY_LINE_STATUS() { return { LOCKED: 0, READY: 1, HALTED: 2 }; }
    static get AI_VERBOSE_LEVEL() { return { SILENCE: 0, CHOSEN_DECISION_ONLY: 1, ALL_CONSIDERATIONS: 2 }; }
    //=================================================================

    static get instance() 
    {
        if (!RoboNationCore._instance)
        {
            RoboNationCore._instance = new RoboNationCore();
        }
        return RoboNationCore._instance;
    }

    constructor()
    {
        super();
    }
    
    get DaysPerMonth() { return this.daysPerMonth; }
    get CurrentDate() { return this.currentDate; }
    get CurrentDay() { return (this.currentDate > 0 && this.currentDate % this.daysPerMonth == 0 ? this.daysPerMonth : (this.currentDate % this.daysPerMonth)); }
    get CurrentMonth() { return Math.ceil((this.currentDate % (this.daysPerMonth*12))/this.daysPerMonth); }
    get CurrentYear() { return Math.ceil(this.currentDate / (this.daysPerMonth*12)); }
    get SalaryIncreaseStep() { return this.salaryIncreaseStep; }
    get CompanyValuationToWin() { return this.valuationToWin;}
    get Winner() { return this.winner; }
    get CurrentEvent() { return this.currentEvent; }

    get DebugLog() { return this._debugLog; }

    init(sendDebugToConsoleLog = true, aiVerboseLevel = 0)
    {
        this.data = {};
        this.initNames();

        this.players = {};
        this.currentEvent = null;

        this._debugLog = "";
        console.oldLog = console.log;
        console.log = function(sendToConsole, consoleFunc, value)
        {
            this._debugLog += value + "\n";
            if (sendToConsole)
            {
                consoleFunc(value);
            }
            this.emit(RoboNationCore.EVENT_DEBUG_LOG, value);
        }
        .bind(this, sendDebugToConsoleLog, console.log.bind(console));

        createCore().then(Module => {
            console.log = console.oldLog;
            this.module = Module;
            this.time = (new Date()).getTime();
            this.isGame = 0;

            this.module.ccall('core_init', null, [], []);

            this.getMaterials();
            this.getRobots();
            this.getResearches();
            this.currentDate = 0;
            this.currentMonth = 0;
            this.daysPerMonth = this.module.ccall('data_getDaysPerMonth', 'number', [], []);
            this.getSalaryIncreaseStep();
            this.getCompanyValuationToWin();

            this.bindedLoop = this.gameLoop.bind(this);
            this.gameLoop();
            
            if (aiVerboseLevel != RoboNationCore.AI_VERBOSE_LEVEL.SILENCE)
            {
                this.setDebugAi(aiVerboseLevel);
            }

            this.emit(RoboNationCore.EVENT_READY);
        });

    }

    gameLoop()
    {
        let now = (new Date()).getTime();
        let delta = (now - this.time) / 1000;
        this.time = now;

        if (this.isGame)
        {
            this.emit(RoboNationCore.EVENT_PRE_LOOP, delta);
        }

        let date = this.module.ccall('core_run', 'number', ['number'], [delta]);
        
        if (this.isGame)
        {
            this.emit(RoboNationCore.EVENT_POST_LOOP, delta);

            if (date > this.currentDate)
            {
                this.currentDate = date;
                this.emit(RoboNationCore.EVENT_DAY_CHANGE, date);

                let month = Math.ceil(date / this.daysPerMonth);
                if (month > this.currentMonth)
                {
                    this.currentMonth = month;
                    this.emit(RoboNationCore.EVENT_MONTH_CHANGE, month);
                }

                let prevEvent = this.currentEvent;
                this.currentEvent = this.getCurrentEvent();
                if (!this.compareEvents(prevEvent, this.currentEvent))
                {
                    if (!prevEvent && this.currentEvent)
                    {
                        this.emit(RoboNationCore.EVENT_CURRENT_EVENT_START, this.currentEvent);
                    }
                    else if (prevEvent && !this.currentEvent)
                    {
                        this.emit(RoboNationCore.EVENT_CURRENT_EVENT_END, prevEvent);
                    }
                    else
                    {
                        this.emit(RoboNationCore.EVENT_CURRENT_EVENT_UPDATE, this.currentEvent);
                    }
                }
            }
            let winner = this.getWinner();
            if (winner)
            {
                this.emit(RoboNationCore.EVENT_GAME_END, winner);
                this.isGame = 0;
            }
        }

        window.requestAnimationFrame(this.bindedLoop);
    }
    
    /*
        Must be an ASCII string
    */
    stringToPtr(str)
    {
        let array = [];
        for (let i = 0; i < str.length; i++)
        {
            array.push(str.charCodeAt(i));
        }
        var ptr = this.module._malloc(str.length)
        this.module.HEAP8.set(array, ptr)
        return ptr
    }

    /*
        Will return an ASCII string
    */
    ptrToString(ptr, length, shouldFree = true) {
        var array = new Int8Array(length)
        array.set(this.module.HEAP8.subarray(ptr, ptr + length));
        if (shouldFree)
        {
            this.module._free(ptr);
        }
        let str = "";
        for (let i = 0; i < length; i++)
        {
            if (array[i] > 0)
            {
                str += String.fromCharCode(array[i]);
            }
            else
            {
                break;
            }
        }
        return str
    }

    arrayToPtr(array, nByte = 4, isUnsigned = false)
    {
        var ptr = this.module._malloc(array.length * nByte)
        this.module["HEAP" + (isUnsigned ? "U" : "") + (nByte*8)].set(array, ptr / nByte)
        return ptr
    }

    ptrToArray(ptr, length, nByte = 4, shouldFree = true, isUnsigned = false) {
        var array = null;

        if (nByte == 8)
        {
            //Built with 32 in mind so the longs are 32 bits for now
            var pos = ptr / 4;
            array = new Int32Array(length);
            array.set(this.module["HEAP" + (isUnsigned ? "U" : "") + "32"].subarray(pos, pos + length));

            /*array = [];
            for (let i = 0; i < length; i++)
            {
                array.push(this.module.getValue(ptr + nByte * i, 'i64'));
            }
            console.log(this.module);*/
        }
        else
        {
            var pos = ptr / nByte;
            array = new Int32Array(length);
            array.set(this.module["HEAP" + (isUnsigned ? "U" : "") + (nByte*8)].subarray(pos, pos + length));
        }
        
        if (shouldFree)
        {
            this.module._free(ptr);
        }
        return array
    }

    /**
     * Converts a 2D array of points into values
     * @param {int} ptr - The pointer to the 2D array
     * @param {int} colLength - The number of columns in the 2D array
     * @param {int} rowLength - The number of rows in the 2D array
     * @param {int} nByte - The number of bytes per value inside a cell
     */
    ptrTo2DArray(ptr, colLength, rowLength, nByte = 4, shouldFree = true, isUnsigned = false) {
        var array2D = [];
        var pointerArray = this.ptrToArray(ptr, rowLength, nByte, false, isUnsigned);
        for (let i = 0; i < pointerArray.length; i++)
        {
            array2D.push(this.ptrToArray(pointerArray[i], colLength, nByte, false, isUnsigned));
        }
        if (shouldFree)
        {
            this.module._free(ptr);
        }
        return array2D;
    }

    /**
     * Clears the debug log received from the core and stored in this class DebugLog property
     */
    clearDebugLog()
    {
        this._debugLog = "";
    }

    /**
     * Initializes the structure for a new player
     * @param {int} playerId - The ID of the player to initialize
     */
    initPlayerStructure(playerId)
    {
        let storage = {};
        let dispatchZone = {};
        let productionLine = {};

        this.players[playerId] = {
            id: playerId,
            name: "",
            nation: 0,
            money: 0,
            warehouse: {
                storage,
                loadingQueue: [/*{timeLeft:0, totalTime:0, materialId:0, quantity:0}*/],
                suppliers: {
                    signed: {},
                    available: {}
                },
                market: {}
            },
            assembly: {
                dispatchZone: {},
                lines: [],
                waitingQueue: [],
                buildList: {}
            },
            research: {

            },
            hr: {
                staffMembers: [],
                applicants: []
            },
            sales: {
                nations: {
                    
                }
            }
        }
    }

    /**
     * Gets if the AI decisions are being debugged through the console or not
     * @returns {boolean} True if the AI decisions are being debugged through the console, false otherwise
     */
    getDebugAi()
    {
        return this.module.ccall('core_getDebugAi', 'number', [], []) > 1;
    }

    /**
     * Sets if the AI decisions should be debugged through the console or not
     * @param {int} debugLevel - 0 = No debug, 1 = Debug the latest decision, 2 = Debug the list of considerations on every action
     */
    setDebugAi(debugLevel)
    {
        return this.module.ccall('core_setDebugAi', null, ['number'], [debugLevel]);
    }

    /**
     * Pauses the game and prevents the core from running
     */
    pause()
    {
        if (this.isGame)
        {
            this.module.ccall('core_pause', null, [], []);
        }
    }

    /**
     * Resumes the game and let the core from running
     */
    resume()
    {
        if (this.isGame)
        {
            this.module.ccall('core_resume', null, [], []);
        }
    }

    /**
     * Checks if a game has been started and if it's currently running
     * @returns {boolean} True if a game has been started and is currently running, false otherwise
     */
    isRunning()
    {
        return this.isGame && this.module.ccall('core_isRunning', 'number', [], []) == 1;
    }

    /**
     * Gets the current speed at which the days are passing
     * @returns {int} The current speed at which the days are passing, mapped to the GAME_SPEED enum
     */
    getGameSpeed()
    {
        return this.module.ccall('core_getDaySpeed', 'number', [], []);
    }

    /**
     * Sets the speed at which the days are passing
     * @param {int} speed - The speed at which the days should pass, should be from the GAME_SPEED enum
     */
    setGameSpeed(speed)
    {
        return this.module.ccall('core_setDaySpeed', null, ['number'], [speed]);
    }

    /* ----------------------------------------------- 
        DATA FUNCTIONS
    -------------------------------------------------*/
    /**
     *  Starts a new game with the given parameters
     * @param {object} params - The parameters for the new game. The object should have the following properties:
     *  - {int} seed - Random seed to apply to the game. Leave at 0 (or omit) for a random seed
     *  - {object} ais - List of AIs to activate in the game. Each AI should have the following properties:
     *      - {string} slot     - Identifier of the slot to place the AI in. First slot starts at 1 and last slot ends at MAX_PLAYER_COUNT - 1. Leave at 0 for no AI
     *      - {int} difficulty  - Difficulty of the AI. Based on the static AI_DIFFICULTY enum
     *      - {int} nation      - Starting nation of the AI. Based on the static NATION enum
     *  - {int} nation - Starting nation of the player. Based on the static NATION enum
     */
    startNewGame(params)
    {
        this.isGame = 1;
        this.players = {};
        this.currentDate = 1;
        this.currentMonth = 1;
        this.currentEvent = null;
        this.initPlayerStructure(1);

        let slots = [];
        let difficulties = [];
        let nations = [];

        let possibleDifficulties = [
            RoboNationCore.AI_DIFFICULTY.EASY,
            RoboNationCore.AI_DIFFICULTY.MEDIUM,
            RoboNationCore.AI_DIFFICULTY.HARD
        ]

        for (let i = 0; i < params.ais.length && i <= RoboNationCore.MAX_PLAYER_COUNT - 1; i++)
        {
            slots.push(params.ais[i].slot);
            nations.push((params.ais[i].slot > 0) ? params.ais[i].nation : 0);
            
            let difficulty = 0;
            if (params.ais[i].slot > 0)
            {
                this.initPlayerStructure(params.ais[i].slot + 1);
                difficulty = params.ais[i].difficulty;
                if (difficulty == 0)
                {
                    difficulty = possibleDifficulties[Math.floor(Math.random() * possibleDifficulties.length)];
                }
            }
            difficulties.push(difficulty);
        }

        this.module.ccall(
            'data_startNewGame', 
            null, 
            [
                //Random seed to use. Leave at 0 for a random seed
                'number',
                //List of AI slots. First slot starts at 1 and last slot ends at 5 
                'number', 
                //List of AI difficulties. Based on the static AI_DIFFICULTY enum
                'number',
                //Starting nation of the player. Based on the static NATION enum
                'number', 
                //List of AI starting nations. Based on the static NATION enum
                'number'
            ], 
            [
                params.seed || 0,
                this.arrayToPtr(slots),
                this.arrayToPtr(difficulties),
                params.nation,
                this.arrayToPtr(nations)
            ]
        );

        this.seed = this.getSeed();
    }

    /**
     * Save the current game in progress. DO NOT CALL THIS METHOD IF NO GAME IS RUNNING. It will surve no purpose and will do nothing
     * @param {int} slotId - The index of the slot where to save the game. Starts at 1 and ends at MAX_SLOT_COUNT 
     * @param {string} name - The name of the saved game. Must be between 1 and SAVE_NAME_MAX_LENGTH characters
     * @param {int} playerId - The identifier of the player saving the game
     * @returns {int} 1 if the game was successfully saved, 0 otherwise
     */
    saveGame(slotId, name, playerId)
    {
        if (this.isGame && slotId >= 1 && slotId <= RoboNationCore.MAX_SLOT_COUNT)
        {
            let charPtr = this.module.ccall('data_saveGame', null, [], []);
            let savedGameStr = this.ptrToString(charPtr, 100000);

            if (name.length > RoboNationCore.SAVE_NAME_MAX_LENGTH)
            {
                name = name.substring(0, RoboNationCore.SAVE_NAME_MAX_LENGTH);
            }

            let dataStr = RoboNationCore.base64encode(JSON.stringify([
                //Name of the save slot
                name,
                //Date of the save
                (new Date()).getTime(),
                //Current playtime when the save was made
                this.getTimePlayed(),
                //In game date when the save was made
                this.getCurrentDate(),
                //Money on-hand of the player when the save was made
                this.getPlayerMoney(playerId),
                //Company valuation of the player when the save was made
                this.getCompanyValuation(playerId)
            ]));
            dataStr += "|" + RoboNationCore.base64encode(savedGameStr);

            window.localStorage.setItem("robonation_slot_" + slotId, dataStr);

            return 1;
        }
        return 0;
    }

    /**
     * Loads a saved game in the local storage into the core
     * @param {int} slotId - The index of the slot to load. Starts at 1 and ends at MAX_SLOT_COUNT
     * @returns {int} 1 if the game was successfully loaded, 0 otherwise
     */
    loadGame(slotId)
    {
        if (slotId >= 1 && slotId <= RoboNationCore.MAX_SLOT_COUNT)
        {
            let dataStr = window.localStorage.getItem("robonation_slot_" + slotId);
            if (dataStr)
            {
                let parts = dataStr.split("|");
                if (parts.length == 2)
                {
                    let meta = JSON.parse(RoboNationCore.base64decode(parts[0]));
                    if (meta)
                    {
                        let savedGameStr = RoboNationCore.base64decode(parts[1]);
                        if (savedGameStr)
                        {
                            let charPtr = this.stringToPtr(savedGameStr);
                            this.module.ccall('data_loadGame', null, ['number'], [charPtr]);
                            this.currentDate = meta[3];
                            this.currentEvent = this.getCurrentEvent();

                            this.seed = this.getSeed();
                            let players = this.getPlayersList();
                            for (let playerId in players)
                            {
                                this.initPlayerStructure(playerId);
                            }
                            this.isGame = 1;

                            return 1;
                        }
                    }
                }
            }
        }
        return 0;
    }

    /**
     * Deletes a previously used save game slot
     * @param {int} slotId Identifier of the slot to delete. Starts at 1 and ends at MAX_SLOT_COUNT
     * @returns {int} 1 if the slot was successfully deleted, 0 otherwise
     */
    deleteSaveSlot(slotId)
    {
        if (slotId >= 1 && slotId <= RoboNationCore.MAX_SLOT_COUNT)
        {
            window.localStorage.removeItem("robonation_slot_" + slotId);
            return 1;
        }
        return 0;
    }

    /**
     * Fetches the meta data of a save slot inside the player's local storage
     * @param {int} slotId - The index of the slot to fetch the meta data from. Starts at 1 and ends at MAX_SLOT_COUNT
     * @returns {object} The meta data of the save slot. The object contains the following properties:
     *                      {
     *                          name: Name given by the player,   
     *                          date: Timestamp (in miliseconds) of when the save was made,
     *                          game: {
     *                              timePlayed: The amount of time played in the game (in seconds),
     *                              date: The date in the game when the save was made,
     *                              money: The amount of money the player had on-hand when the save was made,
     *                              valuation: The valuation of the player's company when the save was made
     *                          }
     *                      }
     *                 Returns null if the slot is empty
     */
    getSaveSlotInfos(slotId)
    {
        if (slotId >= 1 && slotId <= RoboNationCore.MAX_SLOT_COUNT)
        {
            let dataStr = window.localStorage.getItem("robonation_slot_" + slotId);
            if (dataStr)
            {
                let parts = dataStr.split("|");
                if (parts.length == 2)
                {
                    let meta = JSON.parse(RoboNationCore.base64decode(parts[0]));
                    if (meta)
                    {
                        return {
                            name: meta[0],
                            date: meta[1],
                            game: {
                                timePlayed: meta[2],
                                date: meta[3],
                                money: meta[4],
                                valuation: meta[5]
                            }
                        };
                    }
                }
            }
        }
        return null;
    }

    /**
     * Gets the list of all save slots meta data from player's browser local storage. If the slots are empty, they are set as null
     * @returns {object} The list of all save slots meta data. The object contains the following properties:
     *                  {          
     *                     slotId: {    
     *                          name: Name given by the player,   
     *                          date: Timestamp (in miliseconds) of when the save was made,
     *                          game: {
     *                              timePlayed: The amount of time played in the game (in seconds),
     *                              date: The date in the game when the save was made,
     *                              money: The amount of money the player had on-hand when the save was made,
     *                              valuation: The valuation of the player's company when the save was made
     *                          }
     *                      },
     *                      [...]
     *                  }
     */
    getAllSaveSlots()
    {
        let slots = {};
        for (let i = 1; i <= RoboNationCore.MAX_SLOT_COUNT; i++)
        {
            slots[i] = this.getSaveSlotInfos(i);
        }
        return slots;
    }

    /**
     * Gets the seed used to generate random inside the game
     * @returns {int} The number of the seed used
     */
    getSeed()
    {
        return this.module.ccall('core_getSeed', 'number', [], []);
    }

    /**
     * Gets the total time played in the current game
     * @returns {int} The total time played in the current game so far (in seconds)
     */
    getTimePlayed()
    {
        return this.module.ccall('core_getTimePlayed', 'number', [], []);
    }

    /**
     * Gets the current date inside the game
     * @returns {int}    The amount of days passed since the start of the game
     */
    getCurrentDate()
    {
        return this.currentDate;
        //return this.module.ccall('data_getCurrentDate', 'number', [], []);
    }

    /**
     * Gets the list of players inside the current game
     * @returns {object}    The list of players. The players contains the following structure:
     *                      {
     *                          playerId: {
     *                              isAi: true if an AI or false for human players,
     *                              difficulty: AI_DIFFICULTY (0 for human player),
     *                              startingNation: NATION
     *                          }
     *                      }
     */
    getPlayersList()
    {
        let players = {};
        let ptr = this.module.ccall("data_getPlayers", null, [], []);
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 3 * RoboNationCore.MAX_PLAYER_COUNT);
            for (let i = 0; i < array.length; i += 3)
            {
                if (array[i] > 0)
                {
                    players[array[i]] = {
                        isAi: (array[i + 1] > 0),
                        difficulty: array[i + 1],
                        startingNation: array[i + 2]
                    }
                }
            }
        }

        return players;
    }

    /**
     * Gets the progression stats of every players in the game
     * @returns {object}    The list of players progression stats. The players contains the following structure:
     *                      {
     *                          [playerId]: {
     *                              valuation: {
     *                                  current: The current valuation of the player's company,
     *                                  target: Valuation to reach to win the game,
     *                                  penalties: {
     *                                      //The penalties are taken from the PENALTY_TYPE enum
     *                                      [penaltyId]: {
     *                                          startDate: The date when the penalty was applied,
     *                                          duration: The time (in days) the penalty will last starting from the startDate (if less than 0, the penalty is permanent)
     *                                          value: The value of the penalty (% between 0 and 1)
     *                                      }
     *                                  }
     *                              },   
     *                              money: {
     *                                  current: The current amount of money the player has on hand,
     *                                  forecast: A forecast of the amount of money the player will have on hand at the start of the next month,
     *                                  costs: The sum of all costs the player has to pay each month,
     *                                  income: The sum of all revenues the player will receive each month
     *                              },
     *                              research: {
     *                                  progress: The current progress of the ultimate robot research (in %),
     *                                  isBuilding: true if the player is currently building the ultimate robot, false otherwise
     *                              },
     *                              sales: [], //List of identifiers of nations that were cleared by the player
     *                          }
     *                      }
     */
    getPlayersStats()
    {
        let players = {};
        let list = this.getPlayersList();

        for (let playerId in list)
        {
            let valuation = this.getCompanyValuation(playerId);
            let penalties = this.getCompanyValuationPenalties(playerId);
            let money = this.getPlayerBudget(playerId);
            let progress = this.getResearchProgress(playerId, RoboNationCore.ULTIMATE_ROBOT_ID);
            let sales = this.getClearedNations(playerId);

            if (progress <= 0)
            {
                progress = this.getResearchTreeProgress(playerId, RoboNationCore.RESEARCH_TREE.ROBOT);
            }
            
            let isBuilding = false;
            let assemblyLines = this.getAssemblyLines(playerId);
            for (let i = 0; i < assemblyLines.length; i++)
            {
                if (assemblyLines[i].unlocked && assemblyLines[i].content && assemblyLines[i].content.id === RoboNationCore.ULTIMATE_ROBOT_ID)
                {
                    isBuilding = true;
                    progress = 1.0 - assemblyLines[i].timeLeft / assemblyLines[i].totalTime;
                    break;
                }
            }
            if (!isBuilding)
            {
                let waitingSlots = this.getBuildWaitingQueueSlots(playerId);
                for (let i = 0; i < waitingSlots.length; i++)
                {
                    if (waitingSlots[i] && waitingSlots[i].id === RoboNationCore.ULTIMATE_ROBOT_ID)
                    {
                        isBuilding = true;
                        break;
                    }
                }
            }

            players[playerId] = {
                valuation: {
                    current: valuation,
                    target: this.valuationToWin,
                    penalties
                },
                money,
                research: {
                    progress,
                    isBuilding
                },
                sales
            };
        }

        return players;
    }

    /**
     * Calculates the valuation of the given company for the given player
     * @param {int} playerId    The ID of the player to calculate the valuation for
     * @returns {int}           The valuation of the company
     */
    getCompanyValuation(playerId)
    {
        return this.module.ccall('data_getCompanyValuation', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the total company valuation players has to reach to win the game
     * @returns {int}           The total company valuation required to win the game
     */
    getCompanyValuationToWin()
    {
        if (!this.valuationToWin)
        {
            this.valuationToWin = this.module.ccall('data_getCompanyValuationToWin', 'number', [], []);
        }
        return this.valuationToWin;
    }

    /**
     * Gets the amount of money a player has in its possession
     * @param {int} playerId    The ID of the player to get the money from
     * @returns {int}           The amount of money the player has
     */
    getPlayerMoney(playerId)
    {
        return this.module.ccall('data_getPlayerMoney', 'number', ['number'], [playerId]);
    }

    /**
     * Calculates the monthly budget of the given player
     * @param {int} playerId    The ID of the player to calculate the budget for
     * @returns {object}        The monthly budget of the player. The object contains the following structure:
     *                          {
     *                              current: The current amount of money the player has,
     *                              forecast: A forecast of the amount of money the player will have at the start of the next month,
     *                              costs: The sum of all costs the player has to pay each month,
     *                              income: The sum of all revenues the player will receive each month
     *                          }
     */
    getPlayerBudget(playerId)
    {
        let ptr = this.module.ccall('data_getPlayerBudget', 'number', ['number'], [playerId]);

        if (ptr)
        {
            let array = this.ptrToArray(ptr, 4, 8);
            return {
                current: array[0],
                forecast: array[1],
                costs: array[2],
                income: array[3]
            };
        }
    }

    /**
     * Gets the list of every materials inside the game
     * @returns {object}    The list of materials
     */
    getMaterials()
    {
        if (!this.materials)
        {
            let ptr = this.module.ccall('data_getMaterials', 'number', [], []);
            let length = this.module.ccall('data_getMaterialsCount', 'number', [], []);
            let array = this.ptrToArray(ptr, length * 3);

            this.materials = {};
            //We loop through the array to create a structure with the material id as key and it contains the category and rarity
            //The array received from the core is ordered that materials to read are 3 indexes one after the other
            for (let i = 0; i < array.length; i += 3)
            {
                this.materials[array[i]] = {
                    category: array[i + 1],
                    rarity: array[i + 2]
                }
            }

            this.materialsLength = length;
        }

        return this.materials;
    }

    /**
     * Gets the list of every robots inside the game
     * @returns {object}    The list of robots
     */
    getRobots()
    {
        if (!this.robots)
        {
            let ptr = this.module.ccall('data_getRobots', 'number', [], []);
            let length = this.module.ccall('data_getRobotsCount', 'number', [], []);
            let array = this.ptrToArray(ptr, length * 3);

            //We do the same as for the materials but with the robots
            this.robots = {};
            for (let i = 0; i < array.length; i += 3)
            {
                this.robots[array[i]] = {
                    category: array[i + 1],
                    rarity: array[i + 2],
                }
            }

            this.robotsLength = length;
        }

        return this.robots;
    }

    /**
     * Gets the list of every researches inside the game
     * @returns {object}    The list of researches. The researches contains the following structure
     *                      {
     *                          <ID:{
     *                              tree: <researchTree>,
     *                              maxLevel: <researchMaximumLevel>,
     *                              levels: [  //Each index represents a level of the research (level = index + 1)
     *                                  {
     *                                      cost: <moneyCost>,
     *                                      prerequisites: [<ID>, <ID>, ...],
     *                                      subchoices: [
     *                                          {
     *                                              modifier: {
     *                                                  type: <modifierType>,
     *                                                  parameters: [<parameter>, <parameter>, ...],
     *                                                  value: <value>
     *                                              },
     *                                              materials: {
     *                                                  <ID>: {
     *                                                      amount: <materialQuantity>,
     *                                                      rarity: <materialRarity>
     *                                                  },
     *                                                  ...
     *                                              }                        
     *                                          },
     *                                          ...
     *                                      ]
     *                                  }
     *                              ],    
     *                          }
     * 
     *                      }
     */
    getResearches()
    {
        if (!this.researches)
        {
            let ptr = this.module.ccall('data_getResearches', 'number', [], []);
            let length = this.module.ccall('data_getResearchesCount', 'number', [], []);
            let array = this.ptrTo2DArray(ptr, 230, length);

            //We loop through the array to create a structure with the research id as key
            this.researches = {};
            for (let i = 0; i < array.length; i++)
            {
                let maxLevel = array[i][2];
                let levels = [];
                for (let j = 0; j < maxLevel; j++)
                {
                    let subchoices = [];
                    for (let k = 0; k < 2; k++)
                    {
                        let modifier = {
                            type: array[i][40 + k*96 + j*6],
                            parameters: [
                                array[i][41 + k*96 + j*6],
                                array[i][42 + k*96 + j*6],
                                array[i][43 + k*96 + j*6]
                            ],
                            value: array[i][44 + k*96 + j*6] / 1000
                        };
                        let materials = {};
                        for (let l = 0; l < 6; l++)
                        {
                            let id = array[i][70 + k*96 + j*13 + l*2];
                            if (id > 0)
                            {
                                materials[id] = {
                                    amount: array[i][71 + k*96 + j*13 + l*2],
                                    rarity: this.materials[id].rarity
                                };
                            }
                        }
                        subchoices.push({
                            modifier,
                            materials
                        });
                    }

                    let prerequisites = [];
                    for (let k = 0; k < 5; k++)
                    {
                        if (array[i][13 + j*5 + k] > 0)
                        {
                            prerequisites.push(array[i][13 + j*5 + k]);
                        }
                    }
                    
                    levels.push({
                        cost: array[i][3 + j],
                        prerequisites,
                        subchoices
                    })
                }

                this.researches[array[i][0]] = {
                    tree: array[i][1],
                    maxLevel,
                    levels
                }
            }
        }

        return this.researches;
    }

    /**
     * Gets the amount of money a salary is increased each time the player requests a salary increase for an hired staff
     * @returns {int}   The amount of money a salary is increased each time the player requests a salary increase for an hired staff
     */
    getSalaryIncreaseStep()
    {
        if (!this.salaryIncreaseStep)
        {
            this.salaryIncreaseStep = this.module.ccall('data_getSalaryIncreaseStep', 'number', [], []);
        }
        return this.salaryIncreaseStep;
    }

    /**
     * Gets the list of penalties currently applied on a player's company valuation
     * @param {int} playerId Identifier of the player to get the penalties for
     * @returns {object}    The list of penalties currently applied on the player's company valuation. The object contains the following structure:
     *                      {
     *                          //The Ids are taken from the PENALTY_TYPE enum
     *                          [penaltyId]: {
     *                              startDate: The date when the penalty was applied,
     *                              duration: The time (in days) the penalty will last starting from the startDate (if less than 0, the penalty is permanent)
     *                              value: The value of the penalty (% between 0 and 1)
     *                          }
     *                      }
     */
    getCompanyValuationPenalties(playerId)
    {
        let ptr = this.module.ccall('data_getPenalties', 'number', ['number'], [playerId]);

        let penalties = {};
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 4 * (Object.keys(RoboNationCore.PENALTY_TYPE).length - 1));
            
            for (let i = 0; i < array.length; i += 4)
            {
                if (array[i + 1] > 0)
                {
                    penalties[array[i]] = {
                        startDate: array[i + 1],
                        duration: array[i + 2],
                        value: array[i + 3] / 10000
                    }
                }
            }
        }

        return penalties;
    }

    /**
     * Gets the identifier of the player who won the game
     * @returns {int}   The identifier of the player who won the game (null if no one won yet)
     */
    getWinner()
    {
        let ptr = this.module.ccall('data_getWinner', 'number', [], []);
        if (ptr)
        {
            this.winner = this.module.getValue(ptr, 'i32');
        }
        else
        {
            this.winner = null;
        }
        return this.winner;
    }

    /**
     * Gets the identifier of the reason why the game has ended
     * @returns {int}   The identifier of the reason why the game has ended (0 if the game is not over yet). Taken from the WIN_CONDITION enum
     */
    getWinCondition()
    {
        return this.module.ccall('data_getWinCondition', 'number', [], []);
    }

    /**
     * Calculates the stats to display in the end game screen
     * @returns {object}    The stats to display in the end game screen. The object contains the following structure:
     *                      {
     *                          general: {
     *                              winner: The identifier of the player who won the game,
     *                              winConditionId: The win condition of the game, take from the WIN_CONDITION enum,
     *                              timePlayed: The time played by the player (in seconds),
     *                              duration: The duration of the game (in days),
     *                              valuation: The highest company valuation among players,
     *                              production: The highest robot production count among players,
     *                              hr: The highest salary average among players,
     *                              research: {
     *                                  [treeId]: The highest progression among players of that tree,
     *                                  [...]
     *                              },
     *                              sales: {
     *                                  [nationId]: The highest amount of robot sold among players in that nation,
     *                                  [...]
     *                              }
     *                          },   
     *                          players: {
     *                              [playerId]: {
     *                                  valuation: The company valuation of the player,
     *                                  production: The number of robot produced by the player,
     *                                  hr: The average salary of employees inside the player's company,
     *                                  research: {
     *                                      [treeId]: The progression of the player in that tree,
     *                                      [...]
     *                                  },
     *                                  sales: {
     *                                      [nationId]: The amount of robot sold by the player in that nation,
     *                                      [...]
     *                                  }
     *                              },
     *                             [...]    
     *                          }    
     *                      }
     */
    getEndGameStats()
    {
        let stats = {
            general: {
                winner: this.getWinner(),
                winConditionId: this.getWinCondition(),
                timePlayed: this.getTimePlayed(),
                duration: this.getCurrentDate(),
                valuation: 0,
                production: 0,
                hr: 0,
                research: {},
                sales: {},
            },
            players: {}
        };
        let list = this.getPlayersList();

        for (let playerId in list)
        {
            stats.players[playerId] = {
                valuation: this.getCompanyValuation(playerId),
                production: this.getRobotsProducedQuantity(playerId),
                hr: this.getAverageSalary(playerId),
                research: {},
                sales: {}
            };
            if (stats.players[playerId].valuation > stats.general.valuation)
            {
                stats.general.valuation = stats.players[playerId].valuation;
            }
            if (stats.players[playerId].production > stats.general.production)
            {
                stats.general.production = stats.players[playerId].production;
            }
            if (stats.players[playerId].hr > stats.general.hr)
            {
                stats.general.hr = stats.players[playerId].hr;
            }

            //Loop through the robot production and add all the numbers
            let produced = 0;
            for(let robotId in stats.players[playerId].production)
            {
                produced += stats.players[playerId].production[robotId];
            }
            stats.players[playerId].production = produced;

            if (produced > stats.general.production)
            {
                stats.general.production = produced;
            }

            //Loop through the research trees
            for(let treeIndex in RoboNationCore.RESEARCH_TREE) 
            {
                let treeId = RoboNationCore.RESEARCH_TREE[treeIndex];
                if (treeId != RoboNationCore.RESEARCH_TREE.RANDOM)
                {
                    stats.players[playerId].research[treeId] = this.getResearchTreeProgress(playerId, treeId);
                    if (!stats.general.research[treeId] || stats.general.research[treeId] < stats.players[playerId].research[treeId])
                    {
                        stats.general.research[treeId] = stats.players[playerId].research[treeId];
                    }
                }
            };
            //Lets do the same thing for the robot sold in nations
            for(let nationIndex in RoboNationCore.NATION) 
            {
                let nationId = RoboNationCore.NATION[nationIndex];
                if (nationId != RoboNationCore.NATION.RANDOM)
                {
                    stats.players[playerId].sales[nationId] = this.getRobotSoldCount(playerId, nationId);
                    if (!stats.general.sales[nationId] || stats.general.sales[nationId] < stats.players[playerId].sales[nationId])
                    {
                        stats.general.sales[nationId] = stats.players[playerId].sales[nationId];
                    }
                }
            };
        }

        return stats;
    }

    /**
     * Reads and returns the next notification in line for a player
     * @param {int} playerId    Identifier of the player to get the notification for 
     * @returns {object}        The next notification in line for the player. The returned object contains the following structure:
     *                          {
     *                              id: The identifier of the notification,
     *                              groupId: The identifier of the group the notification belongs to (from the NOTIFICATION_GROUP enum),
     *                              date: In-game date when the notificaiton was triggered,
     *                              values: [
     *                                  [value1],
     *                                  [value2],
     *                                  [value3]
     *                              ]
     *                          }
     *                          In case there isn't any notification to read, it returns null
     */
    getNextNotification(playerId)
    {
        let ptr = this.module.ccall('data_readNextNotification', 'number', ['number'], [playerId]);

        let notification = null;
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 5);
            if (array[0] > 0)
            {
                notification = {
                    id: array[0],
                    groupId: Math.floor(array[0]/10),
                    date: array[1],
                    values: [
                        array[2],
                        array[3],
                        array[4]
                    ]
                };
            }
        }
        return notification
    }

    /* ----------------------------------------------- 
        WAREHOUSE FUNCTIONS
    -------------------------------------------------*/
    /**
     * Get the amount of each type of material in the player's warehouse
     * @param {int} playerId    The ID of the player to get the warehouse for
     * @returns {object}        List of materials in the warehouse. The returned object contains the following structure:
     *                          {
     *                              [categoryId]: {
     *                                  [materialId]: {
     *                                     rarity: [materialRarity],
     *                                     amount: [materialAmount],
     *                                     space: {
     *                                         used: [materialUsedSpace],
     *                                         lost: [materialLostSpace]
     *                                      }
     *                                  },
     *                                  [...]
     *                              },
     *                              [...]
     *                          }
     */
    getWarehouseContent(playerId)
    {
        let ptr = this.module.ccall('warehouse_getStorageContent', 'number', ['number'], [playerId]);
        let array = this.ptrTo2DArray(ptr, 4, this.materialsLength);

        let storage = {};

        for (let i = 0; i < array.length; i++)
        {
            if (array[i][1] > 0)
            {
                if (!storage[this.materials[array[i][0]].category])
                {
                    storage[this.materials[array[i][0]].category] = {};
                }
                storage[this.materials[array[i][0]].category][array[i][0]] = {
                    rarity: this.materials[array[i][0]].rarity,
                    amount: array[i][1],
                    space: {
                        used: array[i][2],
                        lost: array[i][3]
                    }
                }
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].warehouse.storage = storage;

        return storage;
    }

    /**
     * Gets the total amount of storage space used by the player
     * @param {int} playerId    The ID of the player to get the warehouse storage space for
     * @returns                 The total amount of storage space used by the player
     */
    getWarehouseTotalUsedSpace(playerId)
    {
        return this.module.ccall('warehouse_getTotalUsedSpace', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the total amount of storage space available to the player
     * @param {int} playerId    The ID of the player to get the warehouse storage space for
     * @returns                 The total amount of storage space available to the player
     */
    getWarehouseTotalAvailableSpace(playerId)
    {
        return this.module.ccall('warehouse_getTotalAvailableSpace', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the total amount of lost storage space by the player
     * @param {int} playerId    The ID of the player to get the warehouse lost storage space for
     * @returns                 The total amount of lost storage space by the player
     */
    getWarehouseTotalLostSpace(playerId)
    {
        return this.module.ccall('warehouse_getTotalLostSpace', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the infos about the current slot in the loading queue
     * @param {int} playerId    The ID of the player to get the loading queue for
     * @returns {array}         The infos about the list of items in the loading queue. The returned object contains the following structure:
     *                          //---Each index represents a slot in the queue---//
     *                          [
     *                              {
     *                                  id: [materialId], //--Returns 0 if the slot is empty--//
     *                                  rarity: [materialRarity],
     *                                  amount: [materialQuantity],
     *                                  timeLeft: [materialTimeLeft],
     *                                  totalTime: [materialTotalTime]
     *                              }
     *                          ]
     */
    getLoadingQueue(playerId)
    {
        let ptr = this.module.ccall('warehouse_getLoadingQueue', 'number', ['number'], [playerId]);
        let loadingQueue = [];

        if (ptr != null)
        {
            let array = this.ptrTo2DArray(ptr, 4, 10);
            //We loop through the array to recreate the structure of the loading queue. We need to check for the getLoadingQueueSlotCount because the array is always 10 elements long
            for (let i = 0; i < array.length && i < this.getLoadingQueueSlotCount(playerId); i++)
            {
                if (array[i][0] > 0)
                {
                    loadingQueue.push({
                        id: array[i][0],
                        rarity: (array[i][0] > 0 ? this.materials[array[i][0]].rarity : 0),
                        amount: array[i][1],
                        timeLeft: array[i][2] / 1000.0,
                        totalTime: array[i][3] / 1000.0
                    });
                }
                else
                {
                    break;
                }
            }
        }
        
        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].warehouse.loadingQueue = loadingQueue;
        
        return this.players[playerId].warehouse.loadingQueue;
    }

    /**
     * Gets the time left to load the current material in the storage
     * @param {int} playerId      The ID of the player to get the loading queue for
     * @returns {float}           The time left to load the current material in the storage (in seconds)
     */
    getLoadingQueueTimeLeft(playerId)
    {
        return this.module.ccall('warehouse_getLoadingQueueTimeLeft', 'number', ['number'], [playerId]) / 1000.0;
    }

    /**
     * Gets the total time to load the current material in the storage
     * @param {int} playerId      The ID of the player to get the loading queue for
     * @returns {float}           The time left to load the current material in the storage (in seconds)
     */
    getLoadingQueueTotalTime(playerId)
    {
        return this.module.ccall('warehouse_getLoadingQueueTotalTime', 'number', ['number'], [playerId]) / 1000.0;
    }

    /**
     * Gets the amount of slots in the loading queue for the player
     * @param {int} playerId    The ID of the player to get the loading queue for
     * @returns                 The amount of slots in the loading queue
     */
    getLoadingQueueSlotCount(playerId)
    {
        return this.module.ccall('warehouse_getLoadingQueueSlotCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the list of available contracts for the player
     * @param {int} playerId    The ID of the player to get the contracts for
     * @returns {object}        List of available contracts. The returned object contains the following structure:
     *                          {
     *                              [contractId]: {
     *                                  canSign: [canSignContract],
     *                                  duration: {
     *                                      min: [contractDurationMin],
     *                                      max: [contractDurationMax]
     *                                  },
     *                                  nextCycle: [contractNextCycle],
     *                                  content: {
     *                                      [materialId]: {
     *                                          rarity: [materialRarity],
     *                                          amount: [materialAmount],
     *                                          price: [materialPrice]
     *                                      },
     *                                      [...]
     *                                  },
     *                              }
     *                              [...]
     *                         }  
     */
    getAvailableContracts(playerId)
    {
        let ptr = this.module.ccall('warehouse_getAvailableSupplierContracts', 'number', ['number'], [playerId]);
        let array = this.ptrTo2DArray(ptr, 34, 10);

        let contracts = {};
        for (let i = 0; i < array.length; i++)
        {
            if (array[i][0] > 0)
            {
                if (!contracts[array[i][0]])
                {
                    contracts[array[i][0]] = {
                        canSign: (array[i][1] == 1),
                        duration: {
                            min: array[i][2],
                            max: array[i][3]
                        },
                        nextCycle: Math.floor(this.CurrentDate / this.daysPerMonth) * this.daysPerMonth + this.daysPerMonth * 2,
                        content: {}
                    };
                }
                for (let j = 4; j < array[i].length; j+=3)
                {
                    if (array[i][j] > 0)
                    {
                        contracts[array[i][0]].content[array[i][j]] = {
                            rarity: this.materials[array[i][j]].rarity,
                            amount: array[i][j+1],
                            price: array[i][j+2]
                        }
                    }
                    else
                    {
                        break;
                    }
                }
            }
            else
            {
                break;
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].warehouse.suppliers.available = contracts;

        return contracts;
    }

    /**
     * Gets the total amount of supplier contract available for the player to sign
     * @param {int} playerId    The ID of the player to get the contracts for
     * @returns {int}           The total amount of supplier contract available for the player to sign
     */
    getAvailableSupplierContractTotalCount(playerId)
    {
        return this.module.ccall('warehouse_getTotalAvailableContracts', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the list of signed contracts for the player
     * @param {int} playerId    The ID of the player to get the contracts for
     * @returns {object}        List of signed contracts. The returned object contains the following structure:
     *                          {
     *                              [contractId]: {
     *                                  startDate: [contractStartDate],
     *                                  duration: [contractDuration], //--In days--//
     *                                  cycleDone: [contractDuration],
     *                                  monthLeft: [contractMonthLeft],//--[(startDate + duration) - currentDate] / this.daysPerMonth--//
     *                                  nextCycle: [contractNextCycle],
     *                                  content: {
     *                                      [materialId]: {
     *                                          rarity: [materialRarity],
     *                                          amount: [materialAmount],
     *                                          price: [materialPrice]
     *                                      },
     *                                      [...]
     *                                  },
     *                              }
     *                              [...]
     *                         }  
     */
    getSignedContracts(playerId)
    {
        let ptr = this.module.ccall('warehouse_getSignedSupplierContracts', 'number', ['number'], [playerId]);
        let array = this.ptrTo2DArray(ptr, 34, 8);

        let contracts = {};
        for (let i = 0; i < array.length; i++)
        {
            if (array[i][0] > 0)
            {
                if (!contracts[array[i][0]])
                {
                    contracts[array[i][0]] = {
                        startDate: array[i][1],
                        duration: array[i][2],
                        cycleDone: array[i][3],
                        monthLeft: Math.floor(array[i][2] / this.DaysPerMonth) - array[i][3],
                        nextCycle: array[i][4],
                        content: {}
                    };
                }
                for (let j = 5; j < array[i].length; j+=3)
                {
                    if (array[i][j] > 0)
                    {
                        contracts[array[i][0]].content[array[i][j]] = {
                            rarity: this.materials[array[i][j]].rarity,
                            amount: array[i][j+1],
                            price: array[i][j+2]
                        }
                    }
                    else
                    {
                        break;
                    }
                }
            }
            else
            {
                break;
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].warehouse.suppliers.signed = contracts;

        return contracts;
    }

    /**
     * Gets the amount of supplier contracts the player has signed simultaneously
     * @param {int} playerId    The ID of the player to get the amount of contracts for
     * @returns                 The amount of supplier contracts the player has signed simultaneously
     */
    getSupplierContractSignedCount(playerId)
    {
        return this.module.ccall('warehouse_getSupplierContractSignedCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the total amount of supplier contracts the player can sign simultaneously
     * @param {int} playerId    The ID of the player to get the amount of contracts for
     * @returns                 The total amount of supplier contracts the player can sign simultaneously
     */
    getSupplierContractSlotCount(playerId)
    {
        return this.module.ccall('warehouse_getSupplierContractSlotTotalCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the time left before the current reshuffle of the available contracts ends. If the reshuffle is already done, it returns 0
     * @param {int} playerId  The ID of the player to get the time left for
     * @returns {float}       The time left before the current reshuffle of the available contracts ends (in seconds)
     */
    getTimeLeftSupplierContractReshuffle(playerId)
    {
        return this.module.ccall('warehouse_getTimeLeftReshuffle', 'number', ['number'], [playerId]) / 1000.0;
    }

    /**
     * Gets the total time before the current reshuffle of the available contracts ends. If the reshuffle is already done, it returns 0
     * @param {int} playerId  The ID of the player to get the total time for
     * @returns {float}       The total time before the current reshuffle of the available contracts ends (in seconds)
     */
    getTotalTimeSupplierContractReshuffle(playerId)
    {
        return this.module.ccall('warehouse_getTotalTimeReshuffle', 'number', ['number'], [playerId]) / 1000.0;
    }

    /**
     * Gets if the player can sign a specific available supplier contract
     * @param {int} playerId    The ID of the player to check the contract for
     * @param {int} contractId  The ID of the contract to check
     * @returns {int}           1 if the player can sign the contract, 0 otherwise
     */
    canSignContract(playerId, contractId)
    {
        return this.module.ccall('warehouse_canStartSupplierContract', 'number', ['number', 'number'], [playerId, contractId]);
    }

    /**
     * Gets the penalty to apply in case a supplier contract gets dropped by the player
     * @param {int} playerId    The ID of the player to get the penalty for
     * @param {int} contractId  The ID of the contract to get the penalty for
     * @returns {object}        An object containing the infos of the penalty. The returned object contains the following structure:
     *                          {
     *                             penalty: [penaltyAmount], //In %, a float between 0 and 1
     *                             duration: [penaltyDuration] //In days
     *                          }
     */
    getSupplierContractDropPenalty(playerId, contractId)
    {
        let ptr = this.module.ccall('warehouse_getDropSupplierContractPenalty', 'number', ['number'], [playerId]);
        let array = this.ptrToArray(ptr, 3);

        return {
            penalty: array[1] / 100.0,
            duration: array[2]
        };
    }

    /**
     * Gets the infos about materials when the player wants to manually buy them from the market
     * @param {int} playerId    The ID of the player to get the infos for
     * @returns {object}        List of materials infos. The returned object contains the following structure:
     *                          {
     *                              [materialId]: {
     *                                category: [materialCategory],
     *                                rarity: [materialRarity],
     *                                price: [materialPrice],
     *                                usedSpace: [materialUsedSpace]
     *                              },
     *                              [...]
     *                          }
     */
    getMaterialMarket(playerId)
    {
        let ptr = this.module.ccall('warehouse_getMaterialMarket', 'number', ['number'], [playerId]);
        let array = this.ptrTo2DArray(ptr, 3, this.materialsLength);

        let materials = {};
        for (let i = 0; i < array.length; i++)
        {
            if (array[i][0] > 0)
            {
                materials[array[i][0]] = {
                    category: this.materials[array[i][0]].category,
                    rarity: this.materials[array[i][0]].rarity,
                    price: array[i][1],
                    usedSpace: array[i][2]
                };
            }
            else
            {
                break;
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].warehouse.market = materials;

        return materials;
    }

    /**
     * Manually buy material from the market
     * @param {int} playerId    The ID of the player to buy the material for
     * @param {int} materialId  The ID of the material to buy
     * @param {int} amount      The amount of material to buy
     * @returns {int}           1 if the material was bought successfully, 0 otherwise           
     */
    buyMaterial(playerId, materialId, amount)
    {
        return this.module.ccall('input_buyMaterial', 'number', ['number', 'number', 'number'], [playerId, materialId, amount]);
    }

    /**
     * Sign a supplier contract from the list of available contracts
     * @param {int} playerId    The ID of the player to sign the contract for
     * @param {int} contractId  The ID of the contract to sign
     * @param {int} duration    The duration of the contract (in days)
     * @returns {int}           1 if the contract was signed successfully, 0 otherwise
     */
    signSupplierContract(playerId, contractId, duration)
    {
        return this.module.ccall('input_signSupplierContract', 'number', ['number', 'number', 'number'], [playerId, contractId, duration]);
    }

    /**
     * Drops a signed supplier contract from the list of signed contracts
     * @param {int} playerId    The ID of the player to drop the contract for
     * @param {int} contractId  The ID of the contract to drop
     * @returns {int}           1 if the contract was dropped successfully, 0 otherwise
     */
    dropSupplierContract(playerId, contractId)
    {
        return this.module.ccall('input_dropSupplierContract', 'number', ['number', 'number'], [playerId, contractId]);
    }

    /**
     * Thrash material from the warehouse
     * @param {int} playerId    The ID of the player to thrash the material for
     * @param {int} materialId  The ID of the material to thrash
     * @param {int} amount      The amount of material to thrash
     * @returns {int}           1 if the material was thrashed successfully, 0 otherwise
     */
    trashMaterial(playerId, materialId, amount)
    {
        return this.module.ccall('input_trashMaterial', 'number', ['number', 'number', 'number'], [playerId, materialId, amount]);
    }

    /**
     * Drops materials from a slot in the warehouse loading queue
     * @param {int} playerId    The ID of the player to drop the materials for
     * @param {int} slotIndex   The ID of the slot to drop the materials from (starts at index 0)
     * @returns {int}           1 if the materials were dropped successfully, 0 otherwise
     */
    cancelLoadingQueueSlot(playerId, slotIndex)
    {
        return this.module.ccall('input_dropMaterialLoadingQueue', 'number', ['number', 'number'], [playerId, slotIndex]);
    }

    /**
     * Reshuffles the list of available supplier contracts
     * @param {int} playerId    The ID of the player to reshuffle the contracts for
     * @returns {int}           1 if the contracts were reshuffled successfully, 0 otherwise
     */
    reshuffleSupplierContracts(playerId)
    {
        return this.module.ccall('input_reshuffleSupplierContractList', 'number', ['number'], [playerId]);
    }
    
    /* ----------------------------------------------- 
        ASSEMBLY FUNCTIONS
    -------------------------------------------------*/
    /**
     * Adds a robot to build on the assembly waiting queue
     * @param {int} playerId    The ID of the player to add the robot for
     * @param {int} robotId     The ID of the robot to add
     * @param {int} amount      The amount of robots to add
     * @returns {int}           1 if the robot was added successfully, 0 otherwise
     */
    addRobotToBuildQueue(playerId, robotId, amount)
    {
        return this.module.ccall('input_buildRobot', 'number', ['number', 'number', 'number'], [playerId, robotId, amount]);
    }

    /**
     * Drops a robot from the assembly waiting queue slot
     * @param {int} playerId    The ID of the player to drop the robot for
     * @param {int} slotIndex   The ID of the slot to drop the robot from (starts at index 0)
     * @returns {int}           1 if the robot was dropped successfully, 0 otherwise
     */
    dropRobotFromBuildQueue(playerId, slotIndex)
    {
        return this.module.ccall('input_dropRobotBuildQueue', 'number', ['number', 'number'], [playerId, slotIndex]);
    }

    /**
     * Gets the amount of space used in the dispatch zone
     * @param {int} playerId    The ID of the player to get the dispatch space used for
     * @returns {int}           The amount of space used in the dispatch zone
     */
    getDispatchSpaceUsed(playerId)
    {
        return this.module.ccall('assembly_getDispatchSpaceUsed', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the amount of lost space in the dispatch zone
     * @param {int} playerId    The ID of the player to get the dispatch lost space for
     * @returns {int}           The amount of space lost in the dispatch zone
     */
    getDispatchLostSpace(playerId)
    {
        return this.module.ccall('assembly_getDispatchLostSpace', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the total amount of space available to the player in the dispatch zone
     * @param {int} playerId    The ID of the player to get the total dispatch space for
     * @returns {int}           The total amount of space available to the player in the dispatch zone
     */
    getDispatchTotalSpace(playerId)
    {
        return this.module.ccall('assembly_getDispatchTotalSpace', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the content of the dispatch zone for the player
     * @param {int} playerId    The ID of the player to get the dispatch zone for
     * @returns {object}        An object containing the content of the dispatch zone. The object contains the following properties:
     *                          {
     *                              [robotCategoryId]: { 
     *                                  [robotId]: {
     *                                      amount: [dispatchZoneQuantity],
     *                                      rarity: [robotRarity],
     *                                      usedSpace: [dispatchZoneUsedSpace],
     *                                      lostSpace: [dispatchZoneLostSpace]
     *                                  }
     *                              }    
     *                          }
     */
    getDispatchRobots(playerId)
    {
        let ptr = this.module.ccall('assembly_getDispatchRobots', 'number', ['number'], [playerId]);
        let array = this.ptrTo2DArray(ptr, 4, this.robotsLength);

        let dispatch = {};
        if (ptr != null)
        {
            for (let i = 0; i < array.length; i++)
            {
                if (array[i][1] > 0)
                {
                    if (!dispatch[this.robots[array[i][0]].category])
                    {
                        dispatch[this.robots[array[i][0]].category] = {};
                    }
                    dispatch[this.robots[array[i][0]].category][array[i][0]] = {
                        rarity: this.robots[array[i][0]].rarity,
                        amount: array[i][1],
                        space: {
                            used: array[i][2],
                            lost: array[i][3]
                        }
                    }
                }
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].assembly.dispatchZone = dispatch;

        return dispatch;
    }

    /**
     * Gets the amount of assembly line the player has unlocked
     * @param {int} playerId    The ID of the player to get the assembly line count for
     * @returns {int}           The amount of assembly line the player has unlocked
     */
    getAssemblyLineCount(playerId)
    {
        return this.module.ccall('assembly_getProductionLineCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the content of the assembly lines for the player
     * @param {int} playerId    The ID of the player to get the content of the assembly lines for
     * @returns {array}         An array containing the content of the assembly lines. Each index represents the assembly line number. Just add 1 since indexes start at 0.
     *                          The array is structured as follows:
     *                          [
     *                              {
     *                                 status: [assemblyLineStatus], //Taken from the ASSEMBLY_LINE_STATUS enum
     *                                 content: {
     *                                      id: [assemblyLineRobotId],
     *                                      amount: [assemblyLineQuantity],
*    *                                      rarity: [robotRarity],
     *                                 }
*    *                                 timeLeft: [assemblyLineTimeLeft],
*    *                                 totalTime: [assemblyLineTotalTime]
     *                              }    
     *                          ]
     */
    getAssemblyLines(playerId)
    {
        let ptr = this.module.ccall('assembly_getProductionLines', 'number', ['number'], [playerId]);

        let lines = [];
        if (ptr != null)
        {
            let array = this.ptrTo2DArray(ptr, 5, this.getAssemblyLineCount(playerId));

            for (let i = 0; i < array.length; i++)
            {
                let content = null;
                if (array[i][1] > 0)
                {
                    content = {
                        id: array[i][1],
                        amount: array[i][2],
                        rarity: this.robots[array[i][1]].rarity
                    };
                }
                lines.push({
                    status: array[i][0],
                    content,
                    timeLeft: array[i][3] / 1000,
                    totalTime: array[i][4] / 1000
                });
            }
        }
        
        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].assembly.lines = lines;

        return lines;
    }

    /**
     * Gets if the build waiting queue is full or not
     * @param {int} playerId    The ID of the player to get the build waiting queue status for
     * @returns {int}           1 if the build waiting queue is full, 0 otherwise
     */
    isBuildQueueFull(playerId)
    {
        return this.module.ccall('assembly_isBuildWaitingQueueFull', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the content of the build waiting queue for the player
     * @param {int} playerId    The ID of the player to get the build waiting queue for
     * @returns {array}         An array containing the content of the build waiting queue. Each index contains the following properties:
     *                         [
     *                            //The index is NULL if the slot is empty
     *                            {
     *                               id: [slotRobotId],
     *                               amount: [slotQuantity],
     *                               rarity: [robotRarity],
     *                               moneyCost: [slotMoneyCost],
     *                               materials: {
     *                                  [materialId]: {
     *                                      amount: [slotMaterialQuantity]
     *                                      rarity: [robotMaterialRarity]
     *                                  }
     *                               }
     *                          ]
     */
    getBuildWaitingQueueSlots(playerId)
    {
        let ptr = this.module.ccall('assembly_getBuildWaitingQueueSlots', 'number', ['number'], [playerId]);

        let slots = [];
        if (ptr != null)
        {
            let array = this.ptrTo2DArray(ptr, 23, 10);

            for (let i = 0; i < array.length; i++)
            {
                let content = null;
                if (array[i][0] > 0)
                {
                    content = {
                        id: array[i][0],
                        amount: array[i][1],
                        rarity: this.robots[array[i][0]].rarity,
                        moneyCost: array[i][2],
                        materials: {}
                    };
                    //Loop for the next 20 indexes to get the materials (every 2 indexes as the first one is the material ID and the second one is the quantity)
                    for (let j = 3; j < 23; j += 2)
                    {
                        if (array[i][j])
                        {
                            content.materials[array[i][j]] = {
                                amount: array[i][j + 1],
                                rarity: this.materials[array[i][j]].rarity
                            };
                        }
                    }
                }
                slots.push(content);
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].assembly.waitingQueue = slots;

        return slots;
    }

    /**
     * Gets the list of robots that the player is allowed to build
     * @param {int} playerId    The ID of the player to get the build list for
     * @returns {array}         An array containing the list of robots the player is allowed to build. Each index contains the following properties:
     *                          {
     *                              [robotId]: {
     *                                  rarity: [robotRarity],
     *                                  canBuild: [robotCanBuild],
     *                                  moneyCost: [robotMoneyCost],
     *                                  space: {
     *                                      used: [robotUsedSpace],
     *                                      lost: [robotLostSpace]
     *                                  },
     *                                  materials: {
     *                                     [materialId]: {
     *                                          amount: [robotMaterialQuantity],
     *                                          owned: [materialCountInWarehouseStorage],
     *                                          rarity: [robotMaterialRarity]
     *                                      }
     *                                  }
     *                              }
     *                          }
     */
    getRobotBuildList(playerId)
    {
        let ptr = this.module.ccall('assembly_getRobotBuildRequirements', 'number', ['number'], [playerId]);

        let list = {};
        if (ptr != null)
        {
            let array = this.ptrTo2DArray(ptr, 23, this.robotsLength);

            for (let i = 0; i < array.length; i++)
            {
                list[array[i][0]] = {
                    rarity: this.robots[array[i][0]].rarity,
                    canBuild: array[i][1],
                    moneyCost: array[i][2],
                    space: {
                        used: array[i][3],
                        lost: array[i][4]
                    },
                    materials: {}
                };
                //Loop for the next 20 indexes to get the materials (every 3 indexes as the first one is the material ID, the second one is the quantity and the third one is the owned quantity)
                for (let j = 5; j < 23; j += 3)
                {
                    if (array[i][j])
                    {
                        list[array[i][0]].materials[array[i][j]] = {
                            amount: array[i][j + 1],
                            owned: array[i][j + 2],
                            rarity: this.materials[array[i][j]].rarity
                        };
                    }
                }
            }
        }

        if (!this.players[playerId])
        {
            this.players[playerId] = this.initPlayerStructure(playerId);
        }
        this.players[playerId].assembly.buildList = list;

        return list;
    }

    /**
     * Gets the amount of each robot produced by a player
     * @param {int} playerId    The ID of the player to get the robots produced quantity for
     * @returns {object}        An object containing the amount of each robot produced by the player. Each property is the robot ID and the value is the amount produced
     */
    getRobotsProducedQuantity(playerId)
    {
        let ptr = this.module.ccall('assembly_getRobotsProducedQuantity', 'number', ['number'], [playerId]);
        
        let amounts = {};
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 2 * this.robotsLength);
            for (let i = 0; i < array.length; i += 2)
            {
                amounts[array[i]] = array[i + 1];
            }
        }
        return amounts;
    }

    /* ----------------------------------------------- 
        RESEARCH AND DEVELOPMENT FUNCTIONS
    -------------------------------------------------*/
    /**
     * Get the current progress of a research tree
     * @param {int} playerId        The ID of the player to calculate the research tree for
     * @param {int} researchTreeId  The ID of the research tree to calculate
     * @returns {float}             The progress of an ongoing research (between 0 and 1), 0 otherwise
     */
    getResearchTreeProgress(playerId, researchTreeId)
    {
        return this.module.ccall('research_getResearchTreeProgress', 'number', ['number', 'number'], [playerId, researchTreeId]) / 10000;
    }

    /**
     * Get the ongoing research of a research tree for a player
     * @param {int} playerId        The ID of the player to calculate the research tree for
     * @param {int} researchTreeId  The ID of the research tree to get its ongoing research
     * @returns {int}               The ID of the ongoing research, 0 otherwise
     */
    getResearchTreeOngoingResearch(playerId, researchTreeId)
    {
        return this.module.ccall('research_getResearchTreeOngoingResearch', 'number', ['number', 'number'], [playerId, researchTreeId]);
    }

    /**
     * Get the current progress of a research
     * @param {int} playerId    The ID of the player to get the progression of the research for
     * @param {int} researchId  The ID of the research to get its progress
     * @returns {int}           The progress of a research between 0 and 1, 0 otherwise
     */
    getResearchProgress(playerId, researchId)
    {
        return this.module.ccall('research_getResearchProgress', 'number', ['number', 'number'], [playerId, researchId])/10000;
    }

    /**
     * Get the current level of a research
     * @param {int} playerId    The ID of the player to get the level of the research for
     * @param {int} researchId  The ID of the research to get its level
     * @returns {int}           The level of a research, 0 if the research has never been leveled before or if it doesn't exist
     */
    getResearchLevel(playerId, researchId)
    {
        return this.module.ccall('research_getResearchLevel', 'number', ['number', 'number'], [playerId, researchId]);
    }

    /**
     * Get the current time left of a research
     * @param {int} playerId    The ID of the player to get the time left for
     * @param {int} researchId  The ID of the research to get its time left
     * @returns {int}           The time left (in seconds) of an ongoing research, 0 otherwise
     */
    getCurrentResearchTimeLeft(playerId, researchId)
    {
        return this.module.ccall('research_getCurrentResearchTimeLeft', 'number', ['number', 'number'], [playerId, researchId]) / 1000;
    }

    /**
     * Get the current total time of a research. Use this method to update the research progression. DO NOT USE to display the time it should takes before the user starts it
     * @param {int} playerId    The ID of the player to get the total time for
     * @param {int} researchId  The ID of the research to get its total time
     * @returns {int}           The total research time (in seconds) of an ongoing research, 0 otherwise
     */
    getCurrentResearchTotalTime(playerId, researchId)
    {
        return this.module.ccall('research_getCurrentResearchTotalTime', 'number', ['number', 'number'], [playerId, researchId]) / 1000;
    }

    /**
     * Get the total time (in seconds) of a research to complete (DO NOT use for progression, use only before starting a research)
     * @param {int} playerId    The ID of the player to get the research time for
     * @param {int} researchId  The ID of the research to get its research time
     * @param {int} level       The level of the research to get its research time
     * @returns {int}           The research time (in seconds) to research a research, 0 otherwise
     */
    getResearchTime(playerId, researchId, level)
    {
        return this.module.ccall('research_getResearchTime', 'number', ['number', 'number', 'number'], [playerId, researchId, level]) / 1000;
    }

    /**
     * Get the amount of money needed to research a research
     * @param {int} playerId    The ID of the player to get the research money cost for
     * @param {int} researchId  The ID of the research to get its research money cost
     * @param {int} level       The level of the research to get its research money cost
     * @returns {int}           The research money cost to research a research, 0 otherwise
     */
    getResearchMoneyCost(playerId, researchId, level)
    {
        return this.module.ccall('research_getResearchMoneyCost', 'number', ['number', 'number', 'number'], [playerId, researchId, level]);
    }

    /**
     * Gets the list of prerequesites for a research and if they have previously been completed or not
     * @param {*} playerId      The ID of the player to get the research prerequesites for
     * @param {*} researchId    The ID of the research to get its research prerequesites
     * @param {*} level         The level of the research to get its research prerequesites
     * @returns {object}        An object containing the prerequesites for a research. The object has the following structure:
     *                          {
     *                              [researchId]: {
     *                                  level: [researchCurrentLevel],
     *                                  goal: [researchGoalLevel],
     *                              }  
     *                          }
     */
    getResearchPrerequesites(playerId, researchId, level)
    {
        let response = {};
        if (this.researches[researchId] && this.researches[researchId].levels[level-1])
        {
            for (let i = 0; i < this.researches[researchId].levels[level-1].prerequisites.length; i++)
            {
                response[this.researches[researchId].levels[level-1].prerequisites[i]] = {
                    level: this.getResearchLevel(playerId, this.researches[researchId].levels[level-1].prerequisites[i]),
                    goal:1
                };
            }
        }
        return response;
    }

    /**
     * Gets the list of materials needed to research a research
     * @param {int} playerId    The ID of the player to get the research material cost for
     * @param {int} researchId  The ID of the research to get its research material cost
     * @param {int} level       The level of the research to get its research material cost
     * @param {int} subchoiceId The identifier of the subchoice to get the research material cost for
     * @returns {Object}        The list of materials needed to research a research, empty object otherwise. The object has the following structure
     *                          {
     *                              <materialId>: {
     *                                  amount: <requiredAmount>,
     *                                  owned: <amountOwnedInStorage>,
     *                                  rarity: <materialRarity>,
     *                              }
     *                          }
     */
    getResearchMaterialCost(playerId, researchId, level, subchoiceId)
    {
        //We call the research_getResearchMaterialCost ccall function and it returns a points where every two indexes is the material id and the amount
        let ptr = this.module.ccall('research_getResearchMaterialCost', 'number', ['number', 'number', 'number', 'number'], [playerId, researchId, level, subchoiceId]);
        let materials = {};
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 18);

            for(let i = 0; i < array.length; i += 3)
            {
                if (array[i] > 0)
                {
                    materials[array[i]] = {
                        amount: array[i + 1],
                        owned: array[i + 2],
                        rarity: this.materials[array[i]].rarity,
                    };
                }
            }
        }

        return materials;
    }

    /**
     * Checks if a research can be started by the player
     * @param {int} playerId    The ID of the player to check the research for
     * @param {int} researchId  The ID of the research to check for
     * @param {int} level       The level the player wants to check if the research can be started
     * @param {int} subchoiceId The identifier of the subchoice to check for. 1 = first subchoice, 2 = second subchoice
     * @returns {int}           1 if the research can be started, 0 otherwise
     */
    canResearch(playerId, researchId, level, subchoiceId = 0)
    {
        return this.module.ccall('research_canResearch', 'number', ['number', 'number', 'number', 'number'], [playerId, researchId, level, subchoiceId]);
    }

    /**
     * Starts a research 
     * @param {int} playerId    The ID of the player to start the research for
     * @param {int} researchId  The ID of the research to start
     * @param {int} subchoiceId The ID of the subchoice to start. 1 = first subchoice, 2 = second subchoice
     * @returns {int}           1 if the research was started successfully, 0 otherwise
     */
    startResearch(playerId, researchId, subchoiceId)
    {
        return this.module.ccall('input_startResearch', 'number', ['number', 'number', 'number'], [playerId, researchId, subchoiceId]);
    }

    /**
     * Stops an ongoing research
     * @param {int} playerId    The ID of the player to stop the research for
     * @param {int} researchId  The ID of the research to stop
     * @returns {int}           1 if the research was stopped successfully, 0 otherwise
     */
    stopResearch(playerId, researchId)
    {
        return this.module.ccall('input_stopResearch', 'number', ['number', 'number'], [playerId, researchId]);
    }

    /* ----------------------------------------------- 
        HUMAN RESOURCES FUNCTIONS
    -------------------------------------------------*/
    /**
     * Gets the current amount of staff members the player has hired in the company
     * @param {int} playerId    The ID of the player to get the staff count for
     * @returns {int}           The number of staff members the player has hired
     */
    getHiredStaffCount(playerId)
    {
        return this.module.ccall('hr_getHiredStaffCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the maximum amount of staff the player can hire in the company
     * @param {int} playerId    The ID of the player to get the staff slot count for
     * @returns {int}           The maximum amount of staff the player can hire in the company
     */
    getHiredStaffSlotCount(playerId)
    {
        return this.module.ccall('hr_getHiredStaffSlotCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the maximum number of applicants that can be generated in the list of applicants
     * @param {int} playerId    The ID of the player to get the maximum number of applicants for
     * @returns {int}           The maximum number of applicants that can be generated in the list of applicants
     */
    getApplicantListTotalCount(playerId)
    {
        return this.module.ccall('hr_getApplicantListTotalCount', 'number', ['number'], [playerId]);
    }

    /**
     * Gets the time left before the reshuffle of the list of applicant finished
     * @param {int} playerId    The ID of the player to get the maximum number of applicants for
     * @returns {int}           The time left before the reshuffle of the list of applicant finished
     */
    getApplicantListReshuffleTimeLeft(playerId)
    {
        return this.module.ccall('hr_getApplicantListReshuffleTimeLeft', 'number', ['number'], [playerId]) / 1000;
    }

    /**
     * Gets the total time to reshuffle of the list of applicant
     * @param {int} playerId    The ID of the player to get the total time to reshuffle of the list of applicant
     * @returns {int}           The total time to reshuffle of the list of applicant
     */
    getApplicantListReshuffleTotalTime(playerId)
    {
        return this.module.ccall('hr_getApplicantListReshuffleTotalTime', 'number', ['number'], [playerId]) / 1000;
    }

    /**
     * Gets the total time to reshuffle of the list of applicant
     * @param {int} playerId    The ID of the player to check if it can hire
     * @param {int} applicantId The ID of the applicant to hire
     * @param {int} salary      The monthly salary to pay the applicant when hired
     * @returns {int}           1 the applicant can be hired, 0 otherwise
     */
    canRecruitApplicant(playerId, applicantId, salary)
    {
        return this.module.ccall('hr_canHireApplicant', 'number', ['number', 'number', 'number'], [playerId, applicantId, salary]);
    }

    /**
     * Gets the list of staff hired in a player's company and their related information
     * @param {int} playerId    The ID of the player to get the list of hired staff from
     * @returns {object}        Object containing the list of hired staff. The object has the following structure:
     *                          {
     *                              [staffId]: {
     *                                  name: [staffName],
     *                                  hiredDate: [staffHiredDate],
     *                                  salary: {
     *                                      current: [staffCurrentSalary],
     *                                      expected: [staffExpectedSalary]
     *                                  },
     *                                  assignation: {
     *                                      department: [staffDepartment],
     *                                      //Always 0 except if the staff is assigned to the Sales department. 
     *                                      //Then the subDepartment is the Nation value
     *                                      subDepartment: [staffSubDepartment]
     *                                  },
     *                                  //All stats are float numbers between 0 and 100
     *                                  stats: {
     *                                      1: [staffWarehouseStat],
     *                                      2: [staffAssemblyStat],
     *                                      3: [staffResearchStat],
     *                                      4: [staffHrStat],
     *                                      5: [staffSalesStat]
     *                                  }       
     *                              }
     *                          }
     */
    getStaffList(playerId)
    {
        let ptr = this.module.ccall('hr_getStaffList', 'number', ['number', 'number'], [playerId, 1]);
        let data = this.ptrTo2DArray(ptr, 12, 10);

        let staff = {};
        for (let i = 0; i < data.length; i++)
        {
            if (data[i][0] > 0)
            {
                let stats = {};
                stats[RoboNationCore.DEPARTMENT.WAREHOUSE] = data[i][5] / 100;
                stats[RoboNationCore.DEPARTMENT.ASSEMBLY] = data[i][6] / 100;
                stats[RoboNationCore.DEPARTMENT.RESEARCH] = data[i][7] / 100;
                stats[RoboNationCore.DEPARTMENT.HUMAN_RESOURCES] = data[i][8] / 100;
                stats[RoboNationCore.DEPARTMENT.SALES] = data[i][9] / 100;

                staff[data[i][0]] = {
                    name: this.getStaffName(data[i][0]),
                    hiredDate: data[i][11],
                    salary: {
                        current: data[i][1],
                        expected: data[i][2]
                    },
                    assignation: {
                        department: data[i][3],
                        subDepartment: data[i][4]
                    },
                    stats
                }
            }
        }

        return staff;
    }

    /**
     * Gets the infos of an hired staff in a player's company
     * @param {int} playerId    The ID of the player who owns the company
     * @param {int} staffId     The ID of the staff member to get the information from
     * @returns {object}        Object containing the infos of the hired staff. The object has the following structure:
     *                             {
     *                               id: [staffId],
     *                                  name: [staffName],
     *                                  hiredDate: [staffHiredDate],
     *                                  salary: {
     *                                      current: [staffCurrentSalary],
     *                                      expected: [staffExpectedSalary]
     *                                  },
     *                                  assignation: {
     *                                      department: [staffDepartment],
     *                                      //Always 0 except if the staff is assigned to the Sales department. 
     *                                      //Then the subDepartment is the Nation value
     *                                      subDepartment: [staffSubDepartment]
     *                                  },
     *                                  //All stats are float numbers between 0 and 100
     *                                  stats: {
     *                                      1: [staffWarehouseStat],
     *                                      2: [staffAssemblyStat],
     *                                      3: [staffResearchStat],
     *                                      4: [staffHrStat],
     *                                      5: [staffSalesStat]
     *                                  }       
     *                              }
     */
    getStaffInfos(playerId, staffId)
    {
        let ptr = this.module.ccall('hr_getStaffInfos', 'number', ['number', 'number', 'number'], [playerId, staffId, 1]);

        let staff = null;
        if (ptr)
        {
            let data = this.ptrToArray(ptr, 12);

            if (data[0] > 0)
            {
                let stats = {};
                stats[RoboNationCore.DEPARTMENT.WAREHOUSE] = data[5] / 100;
                stats[RoboNationCore.DEPARTMENT.ASSEMBLY] = data[6] / 100;
                stats[RoboNationCore.DEPARTMENT.RESEARCH] = data[7] / 100;
                stats[RoboNationCore.DEPARTMENT.HUMAN_RESOURCES] = data[8] / 100;
                stats[RoboNationCore.DEPARTMENT.SALES] = data[9] / 100;

                let staff = {
                    id: data[0],
                    name: this.getStaffName(data[0]),
                    hiredDate: data[11],
                    salary: {
                        current: data[1],
                        expected: data[2]
                    },
                    assignation: {
                        department: data[3],
                        subDepartment: data[4]
                    },
                    stats
                }
            }
        }

        return staff;
    }

    /**
     * Gets the infos of an hired staff assigned to a specific department in a player's company
     * @param {int} playerId        The ID of the player who owns the company
     * @param {int} departmentId    The ID of the department where the staff is assigned to
     * @param {int} subDepartmentId The ID of the subdepartment where the staff is assigned to (only used if the departmentId is Sales, otherwise ommit it)
     * @returns {object}        Object containing the infos of the hired staff. The object has the following structure:
     *                             {
     *                               id: [staffId],
     *                                  name: [staffName],
     *                                  hiredDate: [staffHiredDate],
     *                                  salary: {
     *                                      current: [staffCurrentSalary],
     *                                      expected: [staffExpectedSalary]
     *                                  },
     *                                  assignation: {
     *                                      department: [staffDepartment],
     *                                      //Always 0 except if the staff is assigned to the Sales department. 
     *                                      //Then the subDepartment is the Nation value
     *                                      subDepartment: [staffSubDepartment]
     *                                  },
     *                                  //All stats are float numbers between 0 and 100
     *                                  stats: {
     *                                      1: [staffWarehouseStat],
     *                                      2: [staffAssemblyStat],
     *                                      3: [staffResearchStat],
     *                                      4: [staffHrStat],
     *                                      5: [staffSalesStat]
     *                                  }       
     *                              }
     */
    getStaffAssignedTo(playerId, departmentId, subDepartmentId = 0)
    {
        let ptr = this.module.ccall('hr_getStaffAssignedTo', 'number', ['number', 'number', 'number'], [playerId, departmentId, subDepartmentId]);

        let staff = null;
        if (ptr)
        {
            let data = this.ptrToArray(ptr, 12);
            if (data[0] > 0)
            {
                let stats = {};
                stats[RoboNationCore.DEPARTMENT.WAREHOUSE] = data[5] / 100;
                stats[RoboNationCore.DEPARTMENT.ASSEMBLY] = data[6] / 100;
                stats[RoboNationCore.DEPARTMENT.RESEARCH] = data[7] / 100;
                stats[RoboNationCore.DEPARTMENT.HUMAN_RESOURCES] = data[8] / 100;
                stats[RoboNationCore.DEPARTMENT.SALES] = data[9] / 100;

                staff = {
                    id: data[0],
                    name: this.getStaffName(data[0]),
                    hiredDate: data[11],
                    salary: {
                        current: data[1],
                        expected: data[2]
                    },
                    assignation: {
                        department: data[3],
                        subDepartment: data[4]
                    },
                    stats
                }
            }
        }

        return staff;
    }

    /**
     * Gets the list of applicants in a player's company and their related information
     * @param {int} playerId    The ID of the player to get the list of applicants from
     * @returns {object}        Object containing the list of applicants. The object has the following structure:
     *                          {
     *                              [staffId]: {
     *                                  name: [staffName],
     *                                  canRecruit: [staffCanRecruit],
     *                                  salary: {
     *                                      current: [staffCurrentSalary],
     *                                      expected: [staffExpectedSalary]
     *                                  },
     *                                  //All stats are float numbers between 0 and 100
     *                                  stats: {
     *                                      1: [staffWarehouseStat],
     *                                      2: [staffAssemblyStat],
     *                                      3: [staffResearchStat],
     *                                      4: [staffHrStat],
     *                                      5: [staffSalesStat]
     *                                  }       
     *                              }
     *                          }
     */
    getApplicantList(playerId)
    {
        let ptr = this.module.ccall('hr_getStaffList', 'number', ['number', 'number'], [playerId, 0]);
        let data = this.ptrTo2DArray(ptr, 11, 10);

        let applicants = {};
        for (let i = 0; i < data.length; i++)
        {
            if (data[i][0] > 0)
            {
                let stats = {};
                stats[RoboNationCore.DEPARTMENT.WAREHOUSE] = data[i][5] / 100;
                stats[RoboNationCore.DEPARTMENT.ASSEMBLY] = data[i][6] / 100;
                stats[RoboNationCore.DEPARTMENT.RESEARCH] = data[i][7] / 100;
                stats[RoboNationCore.DEPARTMENT.HUMAN_RESOURCES] = data[i][8] / 100;
                stats[RoboNationCore.DEPARTMENT.SALES] = data[i][9] / 100;

                applicants[data[i][0]] = {
                    name: this.getStaffName(data[i][0]),
                    canRecruit: data[i][10],
                    salary: {
                        current: data[i][1],
                        expected: data[i][2]
                    },
                    stats
                }
            }
        }

        return applicants;
    }

    /**
     * Gets the infos of an applicant in a player's company
     * @param {int} playerId    The ID of the player who owns the company
     * @param {int} applicantId The ID of the applicant to get the information from
     * @returns {object}        Object containing the infos of the applicant. The object has the following structure:
     *                             {
     *                                  id: [staffId],
     *                                  name: [staffName],
     *                                  canRecruit: [staffCanRecruit],
     *                                  salary: {
     *                                      current: [staffCurrentSalary],
     *                                      expected: [staffExpectedSalary]
     *                                  },
     *                                  //All stats are float numbers between 0 and 100
     *                                  stats: {
     *                                      1: [staffWarehouseStat],
     *                                      2: [staffAssemblyStat],
     *                                      3: [staffResearchStat],
     *                                      4: [staffHrStat],
     *                                      5: [staffSalesStat]
     *                                  }       
     *                              }
     */
    getApplicantInfos(playerId, applicantId)
    {
        let ptr = this.module.ccall('hr_getStaffInfos', 'number', ['number', 'number', 'number'], [playerId, applicantId, 0]);

        let applicant = null;
        if (ptr)
        {
            let data = this.ptrToArray(ptr, 11);

            if (data[0] > 0)
            {
                let stats = {};
                stats[RoboNationCore.DEPARTMENT.WAREHOUSE] = data[5] / 100;
                stats[RoboNationCore.DEPARTMENT.ASSEMBLY] = data[6] / 100;
                stats[RoboNationCore.DEPARTMENT.RESEARCH] = data[7] / 100;
                stats[RoboNationCore.DEPARTMENT.HUMAN_RESOURCES] = data[8] / 100;
                stats[RoboNationCore.DEPARTMENT.SALES] = data[9] / 100;

                applicant = {
                    id: data[0],
                    name: this.getStaffName(data[0]),
                    salary: {
                        current: data[1],
                        expected: data[2]
                    },
                    assignation: {
                        department: data[3],
                        subDepartment: data[4]
                    },
                    stats
                }
            }
        }

        return applicant;
    }

    /**
     * Gets the average salary of hired staff inside a player's company
     * @param {int} playerId    The ID of the player to get the average salary from
     * @returns {int}           The average salary of hired staff inside the player's company
     */
    getAverageSalary(playerId)
    {
        return this.module.ccall('hr_getAverageSalary', 'number', ['number'], [playerId]);
    }

    /**
     * Recruits an applicant as a staff member
     * @param {int} playerId    The ID of the player to recruit the applicant for
     * @param {int} applicantId The ID of the applicant to recruit
     * @param {int} salary      The monthly salary to pay the applican when hired
     * @returns {int}           1 if the applicant was recruited successfully, 0 otherwise
     */
    recruitStaff(playerId, applicantId, salary)
    {
        return this.module.ccall('input_recruitStaff', 'number', ['number', 'number', 'number'], [playerId, applicantId, salary]);
    }

    /**
     * Fires an hired staff member
     * @param {int} playerId    The ID of the player to fire the staff member for
     * @param {int} staffId     The ID of the staff member to fire
     * @returns {int}           1 if the staff member was fired successfully, 0 otherwise
     */
    fireStaff(playerId, staffId)
    {
        return this.module.ccall('input_fireStaff', 'number', ['number', 'number'], [playerId, staffId]);
    }

    /**
     * Reshuffles the list of applicants
     * @param {int} playerId    The ID of the player to reshuffle the applicants for
     * @returns {int}           1 if the reshuffle process was successfully started, 0 otherwise
     */
    reshuffleApplicants(playerId)
    {
        return this.module.ccall('input_reshuffleApplicants', 'number', ['number'], [playerId]);
    }

    /**
     * Assigns a staff member to a department
     * @param {int} playerId        The ID of the player to assign the staff member for
     * @param {int} staffId         The ID of the staff member to assign
     * @param {int} department      The ID of the department to assign the staff member to
     * @param {int} subDepartmentId The ID of the subdepartment to assign the staff member to (only used if the departmentId is Sales, otherwise ommit it)
     * @returns {int}               1 if the staff member was assigned successfully, 0 otherwise
     */
    assignStaff(playerId, staffId, department, subDepartmentId = 0)
    {
        return this.module.ccall('input_assignStaff', 'number', ['number', 'number', 'number'], [playerId, staffId, department, subDepartmentId]);
    }

    /**
     * Increases the salary of a staff member. Each time this function is called, the salary is increased by one step
     * @param {int} playerId    The ID of the player to increase the salary for
     * @param {int} staffId     The ID of the staff member to increase the salary for
     * @returns {int}           1 if the salary was increased successfully, 0 otherwise
     */
    increaseSalary(playerId, staffId)
    {
        return this.module.ccall('input_increaseSalary', 'number', ['number', 'number'], [playerId, staffId]);
    }

    /* ----------------------------------------------- 
        SALES FUNCTIONS
    -------------------------------------------------*/
    /**
     * Gets the list of nations unlocked by the player
     * @param {int} playerId    The ID of the player to get the list of unlocked nations for
     * @returns {array}         The list of the identifiers of the unlocked nations
     */
    getUnlockedNations(playerId)
    {
        let ptr = this.module.ccall('sales_getUnlockedNations', 'number', ['number'], [playerId]);
        let array = this.ptrToArray(ptr, (Object.keys(RoboNationCore.NATION).length - 1) * 2);

        let nations = [];
        for (let i = 0; i < array.length; i+=2)
        {
            if (array[i+1] === 1)
            {
                nations.push(array[i]);
            }
        }
        return nations;
    }

    /**
     * Gets the list of nations cleared by the player
     * @param {int} playerId    The ID of the player to get the list of cleared nations for
     * @returns {int}           The list of the identifiers of the cleared nations
     */
    getClearedNations(playerId)
    {
        let ptr = this.module.ccall('sales_getClearedNations', 'number', ['number'], [playerId]);
        let array = this.ptrToArray(ptr, (Object.keys(RoboNationCore.NATION).length - 1) * 2);
    
        let nations = [];
        for (let i = 0; i < array.length; i+=2)
        {
            if (array[i+1] === 1)
            {
                nations.push(array[i]);
            }
        }
        return nations;
    }

    /**
     * Gets the amount of robots to sell as the objective to build a new office
     * @param {int} playerId    The ID of the player to get the amount of robots to sell for
     * @returns {object}        The progress and the target to build a new offce for a given player. The object has the following structure:
     *                          {
     *                              progress: The amount of robots sold
     *                              target: The amount of robots to sell to build a new office
     *                          }
     */
    getNewOfficeProgress(playerId)
    {
        let ptr = this.module.ccall('sales_getNewOfficeObjective', 'number', ['number'], [playerId]);
        let array = this.ptrToArray(ptr, 2);

        return {
            progress: array[0],
            target: array[1]
        };
    }

    /**
     * Gets the list of signed agreements for a player in a specific nation
     * @param {int} playerId    The ID of the player to get the list of signed agreements for
     * @param {int} nationId    The ID of the nation to get the list of signed agreements in
     * @returns {array}         Array where each items is represented by an object with the following structure
     *                          {
     *                              [contractId]: {
     *                                 startDate: The date the agreement was signed
     *                                 duration: The duration of the agreement in days
     *                                 cycleDone: The amount of cycles done so far
     *                                 monthLeft: [agreementMonthLeft],//--[(startDate + duration) - currentDate] / this.daysPerMonth--//
     *                                 nextCycle: Date of the next cycle to ship robots
     *                                 antiSteal: If the antiSteal clause was signed or not
     *                                 governing: If the agreement is a governing one or not
     *                                 stealCost: The cost to steal the agreement,
     *                                 content: {
     *                                     [robotId]: {
     *                                         amount: The quantity of the robot to sell per cycle
     *                                         price: The unit price of the robot to sell per cycle
     *                                         category: The category of the robot
     *                                      }    
     *                                 }
     *                              }
     *                          }
     */
    getSignedAgreements(playerId, nationId)
    {
        let ptr = this.module.ccall('sales_getSignedAgreements', 'number', ['number', 'number'], [playerId, nationId]);
        let data = this.ptrTo2DArray(ptr, 22, 7);

        let agreements = {};
        for (let i = 0; i < data.length; i++)
        {
            if (data[i][0] > 0)
            {
                let agreement = {
                    startDate: data[i][1],
                    duration: data[i][2],
                    cycleDone: data[i][3],
                    monthLeft: Math.floor(data[i][2] / this.DaysPerMonth) - data[i][3],
                    nextCycle: data[i][4],
                    antiSteal: data[i][5],
                    governing: data[i][6],
                    stealCost: data[i][7],
                    content: {}
                };

                for (let j = 8; j < data[i].length; j+=3)
                {
                    if (data[i][j] > 0)
                    {
                        agreement.content[data[i][j]] = {
                            amount: data[i][j+1],
                            price: data[i][j+2],
                            category: this.robots[data[i][j]].category
                        };
                    }
                }
                agreements[data[i][0]] = agreement;
            }
        }

        return agreements;
    }

    /**
     * Gets the list of available agreements for a player in a specific nation
     * @param {int} playerId    The ID of the player to get the list of available agreements for
     * @param {int} nationId    The ID of the nation to get the list of available agreements in
     * @returns {array}         Array where each items is represented by an object with the following structure
     *                          {
     *                              [contractId]: {
     *                                 duration: {
     *                                      min: The minimum amount of days the duration can be,
     *                                      max: The maximum amount of days the duration can be
     *                                  },
     *                                 canSign: If the player can sign this agreement (without the antiSteal clause)
     *                                 nextCycle: The date which the next batch of robots will be taken from the player's dispatch zone
     *                                 antiSteal: Cost of the antiSteal clause
     *                                 governing: If the agreement is a governing one or not
     *                                 conditions: {
     *                                      //Use the AGREEMENT_CONDITION enum to determine the nature of the condition
     *                                      [conditionId]:{
     *                                          value: Value of the condition,
     *                                          isOk: If the condition is met or not
     *                                      }
     *                                 }
     *                                 content: {
     *                                     [robotId]: {
     *                                         amount: The quantity of the robot to sell per cycle
     *                                         price: The unit price of the robot to sell per cycle
     *                                         category: {
     *                                              category: The category of the robot,
     *                                              rarity: The rarity of the robot
     *                                          }
     *                                      }    
     *                                 }
     *                              }
     *                          }
     */
    getAvailableAgreements(playerId, nationId)
    {
        //Lets use the same approach as getSignedAgreements
        let ptr = this.module.ccall('sales_getAvailableAgreements', 'number', ['number', 'number'], [playerId, nationId]);
        let data = this.ptrTo2DArray(ptr, 33, 10);

        let agreements = {};
        for (let i = 0; i < data.length; i++)
        {
            if (data[i][0] > 0)
            {
                let agreement = {
                    duration: {
                        min: data[i][1],
                        max: data[i][2]
                    },
                    canSign: data[i][3],
                    nextCycle: Math.floor(this.CurrentDate / this.daysPerMonth) * this.daysPerMonth + this.daysPerMonth * 2,
                    antiSteal: data[i][4],
                    governing: data[i][5],
                    conditions: {},
                    content: {}
                };

                for (let j = 6; j < 12; j+=3)
                {
                    if (data[i][j] > 0)
                    {
                        agreement.conditions[data[i][j]] = {
                            value: data[i][j+1],
                            isOk: data[i][j+2]
                        };    
                    }
                }

                for (let j = 17; j < data[i].length; j+=3)
                {
                    if (data[i][j] > 0)
                    {
                        agreement.content[data[i][j]] = {
                            amount: data[i][j+1],
                            price: data[i][j+2],
                            category: this.robots[data[i][j]].category
                        };
                    }
                }
                agreements[data[i][0]] = agreement;
            }
        }

        return agreements;
    }

    /**
     * Gets the list of robot prices for a player in a specific nation
     * @param {int} playerId    The ID of the player to get the list of robot prices for
     * @param {int} nationId    The ID of the nation to get the list of robot prices in
     * @returns {object}        Object where each items is represented by an object with the following structure
     *                          <ID>: {
     *                              category: The category of the robot
     *                              price: The price of the robot
     *                              quantity: The quantity of the robot available in the player's dispath zone
     *                          }
     */
    getRobotMarket(playerId, nationId)
    {
        let ptr = this.module.ccall('sales_getRobotsAndPrices', 'number', ['number', 'number'], [playerId, nationId]);
        let data = this.ptrTo2DArray(ptr, 3, 16);

        let robots = {};
        for (let i = 0; i < data.length; i++)
        {
            robots[data[i][0]] = {
                category: this.robots[data[i][0]],
                price: data[i][1],
                quantity: data[i][2]
            };
        }

        return robots;
    }

    /**
     * Gets the infos about the penalty when the player wants to drop a signed agreement
     * @param {int} playerId    The ID of the player to get the penalty for
     * @param {int} nationId    The ID of the nation to get the penalty in
     * @param {int} agreementId The ID of the agreement to get the penalty for
     * @returns {object}        Object with the following structure
     *                          {
     *                             penalty: [penaltyAmount], //In %, a float between 0 and 1
     *                             duration: [penaltyDuration] //In days
     *                          }
     */
    getDropAgreementPenalty(playerId, nationId, agreementId)
    {
        let ptr = this.module.ccall('sales_getDropAgreementPenalty', 'number', ['number', 'number', 'number'], [playerId, nationId, agreementId]);
        let data = this.ptrToArray(ptr, 3);

        return {
            penalty: data[1] / 10000.0,
            duration: data[2]
        };
    }

    /**
     * Gets the amount of supplier contracts the player has signed simultaneously
     * @param {int} playerId    The ID of the player to get the amount of contracts for
     * @param {int} nationId    The ID of the nation to get the amount of contracts for
     * @returns {int}           The amount of supplier contracts the player has signed simultaneously
     */
    getAgreementSignedCount(playerId, nationId)
    {
        return this.module.ccall('sales_getAgreementSignedCount', 'number', ['number', 'number'], [playerId, nationId]);
    }

    /**
     * Gets the total amount of supplier contracts the player can sign simultaneously
     * @param {int} playerId    The ID of the player to get the amount of contracts for
     * @param {int} nationId    The ID of the nation to get the amount of contracts for
     * @returns {int}           The total amount of supplier contracts the player can sign simultaneously
     */
    getAgreementSlotCount(playerId, nationId)
    {
        return this.module.ccall('sales_getAgreementSlotTotalCount', 'number', ['number', 'number'], [playerId, nationId]);
    }

    /**
     * Gets the total amount of supplier contracts the player can sign simultaneously
     * @param {int} playerId    The ID of the player to get the amount of contracts for
     * @param {int} nationId    The ID of the nation to get the amount of contracts for
     * @returns {int}           The total amount of supplier contracts the player can sign simultaneously
     */
    getTotalAvailableAgreementCount(playerId, nationId)
    {
        return this.module.ccall('sales_getTotalAvailableAgreement', 'number', ['number', 'number'], [playerId, nationId]);
    }

    /**
     * Gets the time left before the current reshuffle of the available contracts ends. If the reshuffle is already done, it returns 0
     * @param {int} playerId  The ID of the player to get the time left for
     * @param {int} nationId  The ID of the nation to get the time left for
     * @returns {float}       The time left before the current reshuffle of the available contracts ends (in seconds)
     */
    getTimeLeftAgreementReshuffle(playerId, nationId)
    {
        return this.module.ccall('sales_getTimeLeftReshuffle', 'number', ['number', 'number'], [playerId, nationId]) / 1000.0;
    }

    /**
     * Gets the total time before the current reshuffle of the available contracts ends. If the reshuffle is already done, it returns 0
     * @param {int} playerId  The ID of the player to get the total time for
     * @param {int} nationId  The ID of the nation to get the total left for
     * @returns {float}       The total time before the current reshuffle of the available contracts ends (in seconds)
     */
    getTotalTimeAgreementReshuffle(playerId, nationId)
    {
        return this.module.ccall('sales_getTotalTimeReshuffle', 'number', ['number', 'number'], [playerId, nationId]) / 1000.0;
    }

    /**
     * Get the total amount of robot sold inside a nation by a player
     * @param {int} playerId    The ID of the player to get the amount of robots for
     * @param {int} nationId    The ID of the nation to get the amount of robots for
     * @returns {int}           The total amount of robots sold inside a nation by a player
     */
    getRobotSoldCount(playerId, nationId)
    {
        return this.module.ccall('sales_getRobotSoldCount', 'number', ['number', 'number'], [playerId, nationId]);
    }

    /**
     * Gets if the player can sign a specific available supplier contract
     * @param {int} playerId    The ID of the player to check the contract for
     * @param {int} nationId    The ID of the nation to check the contract for
     * @param {int} agreementId The ID of the agreement to check
     * @param {int} isAntiSteal If the antiSteal clause has to be applied when signing
     * @returns {int}           1 if the player can sign the contract, 0 otherwise
     */
    canSignAgreement(playerId, nationId, agreementId, isAntiSteal)
    {
        return this.module.ccall('sales_canStartAgreement', 'number', ['number', 'number', 'number'], [playerId, nationId, agreementId, (isAntiSteal ? 1 : 0)]);
    }

    /**
     * Gets if the player can steal a specific signed supplier contract from another player
     * @param {int} playerId    The ID of the player who wants to steal the contract
     * @param {int} victimId    The ID of the victim who gets his contract stolen
     * @param {int} nationId    The ID of the nation to check the contract for
     * @param {int} agreementId  The ID of the agreement to check
     * @returns {int}           1 if the player can sign the contract, 0 otherwise
     */
    canStealAgreement(playerId, victimId, nationId, agreementId)
    {
        return this.module.ccall('sales_canStealAgreement', 'number', ['number', 'number', 'number'], [playerId, victimId, nationId, agreementId]);
    }

    /**
     * Manually sells a robot on the market
     * @param {int} playerId    The ID of the player to sell the robot for
     * @param {int} nationId    The ID of the nation to sell the robot to
     * @param {int} robotId     The ID of the robot to sell
     * @param {int} amount      The amount of robots to sell
     * @returns {int}           1 if the robots were sold successfully, 0 otherwise
     */
    sellRobot(playerId, nationId, robotId, amount)
    {
        return this.module.ccall('input_sellRobot', 'number', ['number', 'number', 'number', 'number'], [playerId, nationId, robotId, amount]);
    }

    /**
     * Signs a new partnership agreement from the list of available agreements
     * @param {int} playerId    The ID of the player to sign the agreement for
     * @param {int} nationId    The ID of the nation to sign the agreement in
     * @param {int} agreementId The ID of the agreement to sign
     * @param {int} duration    The duration of the agreement (in days)
     * @param {int} isAntiSteal Whether the agreement should have an anti-steal clause or not
     * @returns {int}           1 if the agreement was signed successfully, 0 otherwise
     */
    signPartnershipAgreement(playerId, nationId, agreementId, duration, isAntiSteal)
    {
        return this.module.ccall('input_signPartnershipAgreement', 'number', ['number', 'number', 'number', 'number', 'number'], [playerId, nationId, agreementId, duration, isAntiSteal]); 
    }

    /**
     * Drops a signed partnership agreement
     * @param {int} playerId    The ID of the player to drop the agreement for
     * @param {int} nationId    The ID of the nation to drop the agreement in
     * @param {int} agreementId The ID of the agreement to drop
     * @returns {int}           1 if the agreement was dropped successfully, 0 otherwise
     */
    dropPartnershipAgreement(playerId, nationId, agreementId)
    {
        return this.module.ccall('input_dropPartnershipAgreement', 'number', ['number', 'number', 'number'], [playerId, nationId, agreementId]); 
    }

    /**
     * Steals a partnership agreement from another player
     * @param {int} stealerId    The ID of the player who steals the agreement
     * @param {int} targetId     The ID of the player who owns the agreement
     * @param {int} nationId     The ID of the nation to steal the agreement from
     * @param {int} agreementId  The ID of the agreement to steal
     * @returns {int}            1 if the agreement was stolen successfully, 0 otherwise
     */
    stealPartnershipAgreement(stealerId, targetId, nationId, agreementId)
    {
        return this.module.ccall('input_stealPartnershipAgreement', 'number', ['number', 'number', 'number', 'number'], [stealerId, targetId, nationId, agreementId]); 
    }

    /**
     * Gets if the player has the requirements to build a new office
     * @param {int} playerId    The ID of the player to check the requirements for
     * @returns {int}           1 if the player has the requirements to build a new office, 0 otherwise
     */
    canBuildOffice(playerId)
    {
        return this.module.ccall('sales_canBuildOffice', 'number', ['number'], [playerId]);
    }

    /**
     * Expands the player's office by unlocking a new nation
     * @param {int} playerId    The ID of the player to expand the office for
     * @param {int} nationId    The ID of the nation to unlock
     * @returns {int}           1 if the office was expanded successfully, 0 otherwise
     */
    buildOffice(playerId, nationId)
    {
        return this.module.ccall('input_expandOffice', 'number', ['number', 'number'], [playerId, nationId]); 
    }

    /**
     * Reshuffles the list of available partnership agreements
     * @param {int} playerId    The ID of the player to reshuffle the agreements for
     * @param {int} nationId    The ID of the nation to reshuffle its agreements
     * @returns {int}           1 if the agreements were reshuffled successfully, 0 otherwise
     */
    reshufflePartnershipAgreements(playerId, nationId)
    {
        return this.module.ccall('input_reshuffleAgreementList', 'number', ['number', 'number'], [playerId, nationId]);
    }

    /* ----------------------------------------------- 
        BANK FUNCTIONS
    -------------------------------------------------*/
    /**
     * Gets the infos about a loan from a player's perspective
     * @param {int} playerId    The ID of the player to use to get the infos
     * @param {int} loanTypeId  The ID of the type of loan to get the infos (LOAN_TYPE enum)
     * @returns {object}        Infos regarding the loan. The object has the following structure:
     *                          {
     *                              type: The type of the loan (LOAN_TYPE enum),
     *                              amount: The amount of money the player will receive if taken,
     *                              valuation: {
     *                                  target: The company valuation the player has to reach before taking it
     *                                  penalty: The penalty applied to the company valuation if the loan is taken (% between 0 and 1)
     *                              },
     *                              takenDate: The last date the player took the loan. If not taken, the value is 0,
     *                              canTake: If the player is allowed to take the loan,
     *                              canRepay: If the player is in position to repay the loan
     *                          }
     */
    getLoanInfos(playerId, loanTypeId)
    {
        let ptr = this.module.ccall('bank_getLoanInfos', 'number', ['number', 'number'], [playerId, loanTypeId]);
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 7, 8);
            
            return {
                type: array[0],
                amount: array[1],
                valuation: {
                    target: array[2],
                    penalty: array[3]/10000
                },
                takenDate: array[4],
                canTake: array[5],
                canRepay: array[6]
            }
        }
    }

    /**
     * Gets the infos about all the loans from a player's perspective
     * @param {int} playerId    The ID of the player to use to get the infos
     * @returns {object}        Infos regarding the loan. The object has the following structure:
     *                          {
     *                              <loanTypeId>: {
     *                                  type: The type of the loan (LOAN_TYPE enum),
     *                                  amount: The amount of money the player will receive if taken,
     *                                  valuation: {
     *                                      target: The company valuation the player has to reach before taking it
     *                                      penalty: The penalty applied to the company valuation if the loan is taken (% between 0 and 1)
     *                                  },
     *                                  takenDate: The last date the player took the loan. If not taken, the value is 0,
     *                                  canTake: If the player is allowed to take the loan,
     *                                  canRepay: If the player is in position to repay the loan
     *                              },
     *                             [...]  
     *                           } 
     */
    getAllLoans(playerId)
    {
        let ptr = this.module.ccall('bank_getAllLoans', 'number', ['number'], [playerId]);
        let loans = {};
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 7 * Object.keys(RoboNationCore.LOAN_TYPE).length, 8);
            for (let i = 0; i < array.length; i += 7)
            {
                loans[array[i]] = {
                    type: array[i],
                    amount: array[i + 1],
                    valuation: {
                        target: array[i + 2],
                        penalty: array[i + 3]/10000
                    },
                    takenDate: array[i + 4],
                    canTake: array[i + 5],
                    canRepay: array[i + 6]
                };
            }
        }

        return loans;
    }

    /**
     * If a player can take a specific type of loan
     * @param {int} playerId    The ID of the player to take the loan for
     * @param {int} loanTypeId  The ID of the type of loan to take (LOAN_TYPE enum)
     * @returns {int}           1 if the loan can be taken, 0 otherwise
     */
    canTakeLoan(playerId, loanTypeId)
    {
        return this.module.ccall('bank_canTakeLoan', 'number', ['number', 'number'], [playerId, loanTypeId]);
    }

    /**
     * If a player can repay a specific type of loan
     * @param {int} playerId    The ID of the player to repay the loan for
     * @param {int} loanTypeId  The ID of the type of loan to take (LOAN_TYPE enum)
     * @returns {int}           1 if the loan can be repaid, 0 otherwise
     */
    canRepayLoan(playerId, loanTypeId)
    {
        return this.module.ccall('bank_canRepayLoan', 'number', ['number', 'number'], [playerId, loanTypeId]);
    }

    /**
     * Take a loan for a player
     * @param {int} playerId    The ID of the player to take the loan for
     * @param {int} loanTypeId  The ID of the type of loan to take (LOAN_TYPE enum)
     * @returns {int}           1 if the loan was taken, 0 otherwise
     */
    takeLoan(playerId, loanTypeId)
    {
        return this.module.ccall('input_takeLoan', 'number', ['number', 'number'], [playerId, loanTypeId]);
    }

    /**
     * Repay a loan for a player
     * @param {int} playerId    The ID of the player to repay the loan for
     * @param {int} loanTypeId  The ID of the type of loan to repay (LOAN_TYPE enum)
     * @returns {int}           1 if the loan was repaid, 0 otherwise
     */
    repayLoan(playerId, loanTypeId)
    {
        return this.module.ccall('input_repayLoan', 'number', ['number', 'number'], [playerId, loanTypeId]);
    }

    /* ----------------------------------------------- 
        EVENT FUNCTIONS
    -------------------------------------------------*/
    /**
     * Fetches the event currently in progress 
     * @returns {object}    The event currently in progress. The object has the following structure:
     *                      {
     *                          id: Identifier of the event,
     *                          outcome: value is from the OUTCOME enum,
     *                          date: {
     *                              start: The date the event started,
     *                              end: The date the event will end, if 0 or less, the event stays for the rest of the game,
     *                          },
     *                          targetValue: Used only in some events, the target of the event (ex. a nation, a material, etc.),
     *                          affectedPlayers: {
     *                              <playerId>: {
     *                                  modifiers: [
     *                                      {
     *                                          type: The type value from the MODIFIER_TYPE enum,
     *                                          parameters: [ //Should always be an array of 3 integers received from the core
     *                                              <integer>, <integer>, <integer>
     *                                          ]    
     *                                          value: The value to apply when modifying
     *                                      },
     *                                      [...]    
     *                                  ]
     *                              }   
     *                          }
     *                      } 
     */
    getCurrentEvent()
    {
        let ptr = this.module.ccall('event_getCurrentEvent', 'number', [], []);
        
        let event = null;
        if (ptr)
        {
            let array = this.ptrToArray(ptr, 26);
            if (array[0] > 0)
            {
                let playerList = this.getPlayersList();
                event = {
                    id: array[0],
                    outcome: array[1],
                    date: {
                        start: array[2],
                        end: (array[3] > 0 ? array[2] + array[3] : 0)
                    },
                    targetValue: array[4],
                    affectedPlayers: {}
                };

                let modifiers = [];
                for (let i = 0; i < 2; i++)
                {
                    if (array[5 + i*5] > 0)
                    {
                        modifiers.push({
                            type: array[5 + i*5],
                            parameters: [
                                array[7 + i*5], 
                                array[8 + i*5], 
                                array[9 + i*5]
                            ],
                            value: array[6 + i*5]/10000
                        });
                    }
                    else
                    {
                        modifiers.push(null);
                    }
                }

                for (let i = 0; i < 6; i++)
                {
                    if (playerList[i+1])
                    {
                        let affectedModifiers = [];
                        if (array[15 + i*2] > 0 && modifiers[0])
                        {
                            affectedModifiers.push(modifiers[0]);
                        }
                        if (array[16 + i*2] > 0 && modifiers[1])
                        {
                            affectedModifiers.push(modifiers[1]);
                        }
                        if (affectedModifiers.length > 0)
                        {
                            event.affectedPlayers[i+1] = {modifiers: affectedModifiers};
                        }
                    }
                }
            }
        }
        return event;
    }

    /**
     * Checks if two events are the same. The structure is taken from the getCurrentEvent function
     * @param {object} event1   First event to check. Can be either NULL or the result of the getCurrentEvent function
     * @param {object} event2   Second event to check. Can be either NULL or the result of the getCurrentEvent function
     * @returns {bool}          True if the events are the same, false otherwise
     */
    compareEvents(event1, event2)
    {
        let isSame = true;
        if ((!event1 && event2) || (!event2 && event1))
        {
            isSame = false;
        }
        else if (event1 && event2)
        {
            isSame = (
                event1.id == event2.id &&
                event1.outcome == event2.outcome &&
                event1.date.start == event2.date.start &&
                event1.date.end == event2.date.end &&
                event1.targetValue == event2.targetValue
            );
            if (isSame)
            {
                for (let playerId in event1.affectedPlayers)
                {
                    if (!event2.affectedPlayers[playerId])
                    {
                        isSame = false;
                        break;
                    }
                }

                if (isSame)
                {
                    for (let playerId in event2.affectedPlayers)
                    {
                        if (!event1.affectedPlayers[playerId] || event1.affectedPlayers[playerId].modifiers.length != event2.affectedPlayers[playerId].modifiers.length)
                        {
                            isSame = false;
                            break;
                        }
                        else
                        {
                            for (let i = 0; i < event1.affectedPlayers[playerId].modifiers.length; i++)
                            {
                                let isIn = false;
                                for (let j = 0; j < event2.affectedPlayers[playerId].modifiers.length; j++)
                                {
                                    if (event1.affectedPlayers[playerId].modifiers[i].type == event2.affectedPlayers[playerId].modifiers[j].type)
                                    {
                                        isIn = true;
                                        break;
                                    }
                                }
                                if (!isIn)
                                {
                                    isSame = false;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }
        return isSame;
    }

    /* ----------------------------------------------- 
        MISC FUNCTIONS
    -------------------------------------------------*/
    /**
     * Gets the translated description of a modifier to display on screen
     * @param {object} modifier                 Modifier to get its translation. The parameter must be structured like this:
     *                                          {
     *                                              type: <integer>, //The type value from the MODIFIER_TYPE enum
     *                                              parameters: [ //Should always be an array of 3 integers received from the core
     *                                                  <integer>, <integer>, <integer>
     *                                              ]    
     *                                              value: <integer>
     *                                          }
     * @param {function} translationFunction    Function to use to translate. Should have only 1 parameter as a string containing the key to translate and returns the translated string
     * @returns {string}                        The translated description of the modifier
    */
    getTranslatedModifierDesc(modifier, translationFunction)
    {
        let percentValue = (val) => { return (val >= 0 ? "+" : "") + (val * 100);}
        let desc = '';
        switch (modifier.type)
        {
            case RoboNationCore.MODIFIER_TYPE.DEMAND:{
                let nation = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.nation.' + modifier.parameters[0] :
                        'data.modifier.all.nations'
                );
                let robot = translationFunction(
                    (modifier.parameters[1]) ? 
                        'data.robot.' + modifier.parameters[1] :
                        'data.modifier.all.robots'
                );
                desc = translationFunction('data.modifier.' + modifier.type + '.desc')
                        .replace('#0#', nation)
                        .replace('#1#', robot)
                        .replace('#2#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.MATERIAL_PRICE:{
                let material = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.material.' + modifier.parameters[0] :
                        'data.modifier.all.materials'
                );
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[1])
                        .replace('#0#', material)
                        .replace('#1#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.ROBOT_PRICE:{
                let robot = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.robot.' + modifier.parameters[0] :
                        'data.modifier.all.robots'
                );
                let nation = translationFunction(
                    (modifier.parameters[2]) ? 
                        'data.nation.' + modifier.parameters[2] :
                        'data.modifier.all.nations'
                );
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[1])
                        .replace('#0#', nation)
                        .replace('#1#', robot)
                        .replace('#2#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.STAFF_BONUS_RESEARCH:
            case RoboNationCore.MODIFIER_TYPE.RESEARCH_MONEY_COST:
            case RoboNationCore.MODIFIER_TYPE.RESEARCH_TIME:{
                let tree = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.research.tree.' + modifier.parameters[0] :
                        'data.modifier.all.trees'
                );
                desc = translationFunction('data.modifier.' + modifier.type + '.desc')
                        .replace('#0#', tree)
                        .replace('#1#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.ROBOT_PRODUCTION_TIME:
            case RoboNationCore.MODIFIER_TYPE.ROBOT_PRODUCTION_MONEY_COST:
            case RoboNationCore.MODIFIER_TYPE.ROBOT_PRODUCTION_MATERIAL_COST:{
                let robot = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.robot.' + modifier.parameters[0] :
                        'data.modifier.all.robots'
                );
                let lineSuffix = (modifier.parameters[1]) ? 'single' : 'all';
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + lineSuffix)
                        .replace('#0#', robot)
                        .replace('#1#', percentValue(modifier.value))
                        .replace('#2#', (modifier.parameters[1] ? modifier.parameters[1] : 0));
            }break;
            case RoboNationCore.MODIFIER_TYPE.STAFF_BONUS_SALES:{
                let nation = translationFunction(
                    (modifier.parameters[0]) ? 
                        'data.nation.' + modifier.parameters[0] :
                        'data.modifier.all.nations'
                );
                desc = translationFunction('data.modifier.' + modifier.type + '.desc')
                        .replace('#0#', nation)
                        .replace('#1#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.ADDED_PARALLEL_CONTRACT:{
                let nation = translationFunction(
                    (modifier.parameters[1]) ? 
                        'data.nation.' + modifier.parameters[1] :
                        'data.modifier.all.nations'
                );
                let value = Math.round((modifier.parameters[2] ? -1 : 1) * modifier.value);
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[0])
                        .replace('#1#', nation)
                        .replace('#0#', (value >= 0 ? "+" : "") + value);
            }break;
            case RoboNationCore.MODIFIER_TYPE.CAN_BUILD_ROBOT:{
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[0])
                        .replace('#0#',  translationFunction('data.robot.' + modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.STAFF_COUNT:
            case RoboNationCore.MODIFIER_TYPE.ADDED_PRODUCTION_LINE:{
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[0])
                        .replace('#0#',  (modifier.value >= 0 ? "+" : "") + Math.round(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.ADDED_STORAGE:
            case RoboNationCore.MODIFIER_TYPE.RESHUFFLE_TIME:
            case RoboNationCore.MODIFIER_TYPE.QUANTITY_PER_CONTRACT:
            case RoboNationCore.MODIFIER_TYPE.QUALITY_PER_CONTRACT:
            case RoboNationCore.MODIFIER_TYPE.LOST_SPACE:{
                desc = translationFunction('data.modifier.' + modifier.type + '.desc.' + modifier.parameters[0])
                        .replace('#0#', percentValue(modifier.value));
            }break;
            case RoboNationCore.MODIFIER_TYPE.STAFF_STATS_GAIN:
            case RoboNationCore.MODIFIER_TYPE.APPLICANT_STATS:
            case RoboNationCore.MODIFIER_TYPE.STAFF_BONUS_WAREHOUSE:
            case RoboNationCore.MODIFIER_TYPE.STAFF_BONUS_PRODUCTION:
            case RoboNationCore.MODIFIER_TYPE.STAFF_BONUS_HUMAN_RESOURCES:
            case RoboNationCore.MODIFIER_TYPE.EXPECTED_SALARY:
            case RoboNationCore.MODIFIER_TYPE.CURRENT_SALARY:
            case RoboNationCore.MODIFIER_TYPE.LOADING_QUEUE_TIME:{
                desc = translationFunction('data.modifier.' + modifier.type + '.desc')
                        .replace('#0#', percentValue(modifier.value));
            }break;
        }

        let descParts = desc.split('#b#');
        desc = descParts[0];
        if (descParts.length > 1)
        {
            for (let i = 1; i < descParts.length; i++)
            {
                if (i % 2 == 1)
                {
                    desc += '<span class="important">' + descParts[i];
                }
                else
                {
                    desc += '</span>' + descParts[i];
                }
            }
        }

        return desc;
    }

    /**
     * Function to generate the translated text to display regarding a notification
     * @param {object} notification     Notification object received from the getNextNotification function. It should have the following structure:
     *                                  {
     *                                      id: The identifier of the notification,
     *                                      groupId: The identifier of the group the notification belongs to (from the NOTIFICATION_GROUP enum),
     *                                      date: In-game date when the notificaiton was triggered,
     *                                      values: [
     *                                          [value1],
     *                                          [value2],
     *                                          [value3]
     *                                      ]
     *                                  }
     * @param {*} translationFunction   Function to use to translate. Should have only 1 parameter as a string containing the key to translate and returns the translated string
     * @returns {object}                An object containing the translated description and a good/bad state of the notification:
     *                                  {
     *                                      desc: <TRANSLATED_DESCRIPTION>,
     *                                      good: <1 FOR GOOD, 0 FOR BAD>
     *                                  }
     */
    getTranslatedNotificationDesc(notification, translationFunction)
    {
        if (!this.negativeNotifications)
        {
            this.negativeNotifications = [
                //WAREHOUSE
                10 /*WarehouseLoadingQueueFull*/, 11 /*WarehouseStorageCannotAdd*/, 12 /*WarehouseSupplierContractFailed*/,
                //ASSEMBLY
                20 /*AssemblyLineStopped*/, 21 /*AssemblyDispatchFull*/,
                //RESEARCH
                // (none)
                //HUMAN RESOURCES
                40 /*HRStaffQuitExpectedLow*/, 41 /*HRStaffQuitNoPay*/,
                //SALES
                50 /*SalesPartnershipFailed*/, 54 /*SalesPartnershipStolen*/
                //EVENTS
                // (none)
                //MARKET
                // (none)
                //BANK
                // (none)
            ];
        }

        let str = translationFunction('data.notification.' + notification.id);
        switch (notification.id)
        {
            //--------------------------------
            // GROUP: GENERAL
            //--------------------------------
            //Nothing special here

            //--------------------------------
            // GROUP: WAREHOUSE
            //--------------------------------
            //Nothing special here

            //--------------------------------
            // GROUP: ASSEMBLY
            //--------------------------------
            case 20: //AssemblyLineStopped
                str = str.replace('#0#', notification.values[0]);
                break;

            //--------------------------------
            // GROUP: RESEARCH
            //--------------------------------
            case 30: //ResearchCompleted
                str = str.replace('#0#', translationFunction('data.research.' + notification.values[0]));
                break;

            //--------------------------------
            // GROUP: HUMAN_RESOURCES
            //--------------------------------
            case 40: //HRStaffQuitExpectedLow
            case 41: //HRStaffQuitNoPay
                str = str.replace('#0#', this.getStaffName(notification.values[0]));
                break;

            //--------------------------------
            // GROUP: SALES
            //--------------------------------
            case 50: //SalesPartnershipFailed
            case 51: //SalesPartnershipEnded
            case 52: //SalesPartnershipReshuffled
            case 53: //SalesPartnershipGoverning
                str = str.replace('#0#', translationFunction('data.nation.' + notification.values[0]));
                break;
            case 54: //SalesPartnershipStolen
                str = str.replace('#0#', translationFunction('data.nation.' + notification.values[0]));
                str = str.replace('#1#', translationFunction('data.nation.' + notification.values[1]));
                break;

            //--------------------------------
            // GROUP: EVENTS
            //--------------------------------
            case 60: //EventEnded
                str = str.replace('#0#', translationFunction('data.event.' + notification.values[0]));
                break;

            //--------------------------------
            // GROUP: MARKET
            //--------------------------------
            case 70: //CompanyValuationMilstoneReached
                str = str.replace('#0#', notification.values[0]);
                str = str.replace('#1#', (Math.round(notification.values[1] / 100)));
                break;
            case 71: //CompanyResearchMilstoneReached
                str = translationFunction('data.notification.' + notification.id + (notification.values[2] ? '.end' : '.start'));
                str = str.replace('#0#', notification.values[0]);
                break;
            case 72: //CompanyNationCleared
                str = str.replace('#0#', notification.values[0]);
                str = str.replace('#1#', translationFunction('data.nation.' + notification.values[1]));
                break;

            //--------------------------------
            // GROUP: MARKET
            //--------------------------------
            //Nothing special here
        }
        
        return {
            desc: str,
            good: (this.negativeNotifications.indexOf(notification.id) < 0)
        };
    }

    /**
     * Function to generate the translated text to display regarding an event
     * @param {object} event            Event received from the getCurrentEvent function (or the CurrentEvent property)
     * @param {boolean} isUpdate        True if the event is an update of the current event, false otherwise
     * @param {*} translationFunction   Function to use to translate. Should have only 1 parameter as a string containing the key to translate and returns the translated string
     * @returns {string}                An object containing the following properties:
     *                                  {
     *                                      "title": Translated title of the event,
     *                                      "description": Translated description of the event,
     *                                  }
     */
    getTranslatedEvent(event, isUpdate, translationFunction)
    {
        let title = translationFunction('data.event.' + event.id);
        let description = translationFunction('data.event.' + event.id + '.desc');
        switch (event.id)
        {
            case 1: //ROBOTIC EXPO
            case 2: //NEW MARKET OPPORTUNITY
            case 8: //ENVIRONMENTAL DISASTER
                description = description.replace('#0#', translationFunction('data.nation.' + event.targetValue));
                break;
            case 4: //NEW SUPPLIER IN THE MARKET
            case 6: //MATERIAL SHORTAGE
                description = description.replace('#0#', translationFunction('data.material.' + event.targetValue));
                break;
            case 10: //ESPIONAGE
                description = description.replace('#0#', translationFunction('data.research.tree.' + event.targetValue));
                break;
            case 7: //CYBERSECURITY THREAT
                if(isUpdate)
                {
                    description = translationFunction('data.event.' + event.id + '.update');
                    for (let id in event.affectedPlayers)
                    {
                        for (let i = 0; i < event.affectedPlayers[id].modifiers.length; i++)
                        {
                            if (event.affectedPlayers[id].modifiers[i].type == RoboNationCore.MODIFIER_TYPE.ROBOT_PRODUCTION_TIME)
                            {
                                description = description.replace('#0#', event.affectedPlayers[id].modifiers[i].parameters[1]);
                                break;
                            }
                        }
                        break;
                    }
                }
                break;
            case 9: //UNION STRIKE
                if(isUpdate)
                {
                    description = translationFunction('data.event.' + event.id + '.update');
                    for (let id in event.affectedPlayers)
                    {
                        for (let i = 0; i < event.affectedPlayers[id].modifiers.length; i++)
                        {
                            if (event.affectedPlayers[id].modifiers[i].type == RoboNationCore.MODIFIER_TYPE.CURRENT_SALARY)
                            {
                                description = description.replace('#0#', event.affectedPlayers[id].modifiers[i].value * 100);
                                break;
                            }
                        }
                        break;
                    }
                }
                break;
        }

        return { title, description };
    }

    /**
     * Picks a name for a staff member based on its id
     */
    getStaffName(staffId)
    {
        return this.data.names[(parseInt(staffId) + this.seed) % this.data.names.length];
    }

    initNames()
    {
        this.data.names = ["Alexander Braun","Susanne Heinrich","Gabrielle Mercier","Lyubov Morozova","Gage Wilson","Saki Nagasaki","Shin Se-kyung","James Walker","Manon Philippe","Noah Singh","Sophie Dufour","Oscar Alvarez","Ulysse Durand","Mina Tanaka","Park Hong-seok","Seok Hoon Kim","Manuel Cruz","Benoit Denis","Silina Ivanova","Hiroshi Tanaka","Florian Sch\u00e4fer","Emily Brooks","Susana Hernandez","Maho Sato","Saeed Salah","Yumi Takahashi","Nawaf Al-Dhafiri","Kang Yeon-joo","Genevi\u00e8ve Ch\u00e9nier","Anna Kuznetsova","Jes\u00fas Dom\u00ednguez","Nikita Sokolov","Kousuke Sasaki","Lim Seung-jae","Afaf Al-Saeed","Misa Suzuki","Khuloud Al-Saeed","Juan Gutierrez","Reiko Nakagawa","Muna Al-Khaldi","Ibtisam Al-Ghamdi","Saki Nishimura","Octavio Sanchez","Mathilde Dupont","Irina Ivanova","Jung Tae-sung","Francis Lavoie","Kim Saeng-min","Julia Garc\u00eda","R\u00e9mi Foucher","Park Jae-eun","Louis Lemoine","Eduardo Herrera","Oh Seung-hee","Roberto Alonso","Isabel Schulze","Kimberly Ramirez","Tatiana Ivanov","Thomas Garcia","Yun Jae-hee","Marisol Vega","Menna Al-Attar","Kang Sung-hoon","Tamer Hassan","Adnan Al-Khouri","Artem Popov","Konstantin Popov","Sang Woo Kim","Ana Bel\u00e9n L\u00f3pez","Carson Hayes","Enrique P\u00e9rez","Olivier Gautier","Majid Al-Rashid","Sophia Wong","Pavel Petrov","Vladislav Alekseev","Seung Hyun Lee","Pavel Nikolaeva","Yulia Novak","Avery Brown","Luc\u00eda Fern\u00e1ndez","Konstantinova Anna","Aida Hassan","Noah Wilson","Adam Young","Dominik Seidel","Stella Morris","\u00c1ngel Ortiz","\u00d3scar Gallego","Nada Mohamed","Laila Al-Hassan","Tatiana Romanova","Shireen Al-Hammadi","Elizabeth Thompson","Marina Jimenez","Genevi\u00e8ve Dub\u00e9","Konstantineva Yana","Patrick Gagn\u00e9","Moritz Schr\u00f6der","Maryam Al-Kuwari","Oscar Fern\u00e1ndez","Alina Huber","Romain Barbier","Daniel Hofmann","Pascal Lambert","Dana Al-Maadeed","Paula Schiller","Victor Castro","Yuri Sokolov","Isabelle Dupont","Mahmoud El-Garhy","Risa Nakamura","Jean-Fran\u00e7ois Gosselin","Pablo Jimenez","Shin Seung-ju","Beatriz Sanchez","Kim Se-jeong","Lee Eun-ji","Sophie Schuster","Elena Sidorova","Carlos Jim\u00e9nez","Hannah Scholz","David Barnes","Patrick C\u00f4t\u00e9","Dae Hee Kang","Kevin Turner","Lee Sun Gyun","Song Hyun-woo","Xavier Wright","Nam Tae Hyun","Carlos Rodr\u00edguez","Sara Garc\u00eda","Luisa Rodriguez","Roula Saleh","Hassan Al-Busaidi","Irina Yakovleva","Doaa Gamal","Natalia Kovalenko","Ahmed Samy","Khaled Bassiouny","Min Hee Lee","Kang Hoon-jin","Jamil Al-Khawaja","Kirill Golubev","Jung Kyung Ho","Konstantin Romanov","Quim Gil","Lee Hae-won","Stephanie Meier","Louis Braun","Yaser Al-Amiri","Sof\u00eda Gonz\u00e1lez","Rachel Lee","Larisa Smirnova","Roc\u00edo Fern\u00e1ndez","Sohaila Ahmed","Kojiro Tanaka","Oh Dong-hwa","Lee Kyu-han","Irina Belova","Evan Gonzalez","Luca Vega","Haruki Suzuki","Kyouko Tanaka","Olivia Klein","Jae Wook Park","Chieko Kimura","Gemma Herrera","Landon Adams","Stefanie Hoffmann","Takehiro Sato","Alexander Smirnov","Khadija Al-Raisi","Robert Schr\u00f6der","Martin Espinoza","Oh Su-yeon","Verena Vogt","Yulia Petrova","Hiroko Matsuo","Silvia D\u00edaz","Mayumi Ishida","Maria Fedorova","Holger Schreiner","Choi Kyung-jin","Mireya Alvarez","Christian Thiel","Julia Fischer","Paisley Coleman","Nawal Al-Qadi","Akira Tanaka","Evgeniya Ivanova","Svetlana Smirnova","Aiko Takahashi","Jeong Seo-yeon","Jeong Jun-woo","Keiko Nakamura","Akane Suzuki","Vanessa Scholz","Moon Chae-won","Olivier Gauthier","Florian K\u00fchn","Kirill Smirnov","Daisuke Sato","Adrian Castro","Yaser Mansour","Ksenia Ivanova","Seung Hyun Kim","Anna Bogdanova","Alexandra Davis","Sylvia Schwarz","Miyuki Yamamoto","Nada Al-Awadhi","Kim Won-woo","Mayumi Kimura","Kim Seung-soo","Vladimir Ivanov","Luciana Vega","Samir Al-Marzouq","Yasmin Al-Hakim","Seo In-guk","Kwon Do-yeon","Robert Durand","Ethan Robinson","Tariq Al-Nasser","Kim Sung-oh","Roberto Ramos","Lee Kwang-jae","Keita Nakamura","Michael Rodriguez","Jae Yeon Park","Sara Al-Sawwaf","Song Kyung-ho","Jaime L\u00f3pez","Noriko Nakamura","Ryota Tanaka","Liliana Valdez","Paula K\u00f6nig","Natalia G\u00f3mez","Johannes M\u00fcller","Nico M\u00fcller","Jasmine Campbell","Jenny Fuchs","Grace Carter","Nikolai Petrov","Yannick Barreau","Anja Bach","Karin K\u00f6hler","Mahmoud Fawzi","Jacqueline Schuster","Thomas Collins","Dalal Al-Mohannadi","Olga Popova","Reem Al-Kuwari","Naoki Tanaka","Haya Al-Ruwaili","Miki Kato","Santiago Fern\u00e1ndez","Nikolay Voronin","Olga Saez","Maryam Akram","Isamu Yoshida","Linda Reimann","Aki Nakamura","Miriam Martin","Koichi Sato","Sabine Braun","Turki Al-Johani","Park Bo Gum","Hussein Al-Mutawa","Hannah Patel","Yumi Nakamura","Hiroki Saito","Michel Rousseau","Rina Suzuki","Hyun Sung Park","Jonathan Turcotte","Quentin Laurent","Abdul Rahman Khader","Lee Soo-min","Yun Sang-hoon","Megumi Nakamura","Andres Fernandez","Marie-Jos\u00e9e C\u00f4t\u00e9","Marine Gautier","Michiko Tanaka","Nour Ayman","Jerome Henry","Han Ga In","Xander Flores","Antoine Huet","Natalia Solano","Mackenzie Allen","Takuma Ishikawa","Rosa Medina","Ivan Garc\u00eda","Madison Green","Maxwell Smith","Pia Wirth","Patrick Richter","Rashid Al-Mansoori","Misa Kimura","Laura Schmitz","Lama Al-Sulaiman","Benjamin Lecomte","Roman Ivanov","Ximena Wood","Marie Schulte","Jung Il-woo","Keisuke Yamaguchi","Park Ju-yeon","Nina Krause","Majid Al-Qahtani","Theresa Schmitt","Robert M\u00fcller","Artem Petrov","Eli King","Safa Mamdouh","Ma\u00eblys Brunet","Greta Sch\u00e4fer","Nikolai Sokolov","Orlov Alexander","Choi Hye-jin","Wael Ahmed","Joachim Schultheis","Elena Prokhorova","Ayman Osama","Youssef Ali","Rahma Al-Sharif","Beatriz Hernandez","R\u00e9mi Deschamps","Song Tae-yong","Kuzmin Ivanov","Renato Vargas","Evelyn Marshall","Stefan Meyer","Raphaela Vogt","Saad Al-Ghanim","Lim Su Jin","Carmen Ramos","Jang Ji-eun","Galina Ivanova","Sara Cruz","Ahn Min-ho","Kyoko Kobayashi","Michiko Nakamura","Omar Al-Ali","Kim Ji Soo","Lim Hye-won","Daria Volkova","Jin Woo Kim","Isabella Reyes","Yoko Ishikawa","Yukio Ishida","Kumiko Sato","Tatiana Kovalenko","Sara Collins","David Allen","Naoki Nakamura","Lina Baum","Lee Ha-kyung","Jade Masson","Laura Henry","Hyun Joo Lee","Kirill Vasilev","Leah Murphy","Cristina D\u00edaz","Shin Yeon-woo","Alexander K\u00f6hler","Fabian Zimmermann","Sofia Petrova","Jordan Allen","Miriam L\u00f3pez","David Vetter","Grace Turner","Nikolai Kozlov","Rapha\u00ebl G\u00e9rard","Jang Hae-jin","Maya Scott","Artem Kuznetsov","Maria K\u00f6nig","Takeshi Suzuki","Nadezhda Ivanova","Bae Suzy","Lola Serrano","Masaaki Nakamura","Liam Garcia","Elena Schumacher","Samantha Kim","Evgeniya Kuznetsova","Olivia Martin","Lee Hye-ri","Sergeeva Ivanova","Jin Ho Kim","Nadia Al-Hakim","Marco Seidel","Alina Kozlova","Olivia Taylor","Manon Gagnon","Sophie Aubin","Nesma Osama","Julian Taylor","Jackson Hernandez","Raphaela Schmid","Hamed Al-Dafiri","Chihiro Yamada","Ursula Schulte","Gilles Petit","Patrick Keller","Irina Gorbacheva","Han Soo-bin","Vladislav Popov","Anastasia Morozova","Tristan Kim","Birte Schmitz","Masako Takahashi","Darya Voronina","Sang Min Kim","Yahya Al-Dosari","Annabelle Parker","Vladimir Kuznetsov","Scarlett Wright","Lucas Vega","Serafima Ivanova","Guillermo Vega","Adel Al-Tuwaijri","Mireille L\u00e9gar\u00e9","Francis Pelletier","Mazen Ali","Byung Jin Park","Laura Keller","Philippe St-Pierre","Laure Fontaine","Yoo Seung Ho","Arnaud Brun","Xenia Hartmann","Evgeny Sokolov","Olivia Hubert","Valerya Efimova","Hossam Amr","Yoon Shi-yoon","Ha Yeon Soo","Avery King","Alexander Pfeiffer","Song Hyo-jin","Park Seo-joon","Nicole Lehmann","Takako Ishida","Saori Ito","Laila Gamal","Said Elham","Mar\u00eda Isabel Mart\u00ednez","Son Ye Jin","Mufid Al-Dulaimi","Victoria King","Mai Nakano","Mizuki Nakamura","Waleed Al-Hamdan","Kathleen Lange","Aya Takahashi","Vincent Fournier","Kyung Soo Lee","Yannick Reynaud","Masahiro Ito","Sultan Al-Suwaidi","Tariq Al-Muhanna","Majid Abdelrazek","Pascal Morel","Choi Soo-jin","Kazuki Suzuki","Alyona Popova","Mateo Delgado","Maryam Nabil","Moon Soo-ah","Melanie Sommer","Nora Ahmed","Chika Nakamura","Kuznetsova Ivanova","Choi Seung Ho","Elisa Fern\u00e1ndez","Oh Jae-won","Saya Suzuki","Melanie Durand","Scarlett Turner","Benoit Mercier","Kimiko Tanaka","Carina Pfeiffer","Miki Ishikawa","Jessie Chen","Denis L\u00e9ger","Ivanova Petrova","Jun Hyun-moo","Marina Petrova","Elodie Roussel","Pablo Santos","Ivanenko Petrov","Galina Antonova","Marko Peters","Hiroshi Saito","Laura Taylor","Diane Moreau","Yana Kuznetsova","Nicholas James","Megumi Sato","Jorge Moreno","Sofia Cook","Marie-Jos\u00e9e Gagnon","Sophie Mercier","Benjamin Weber","Vladislav Ivanov","Santiago Castro","Irina Kuznetsova","Francisco Flores","Isabel Ortiz","Jae Young Jung","Roman Fedotov","Jung Ye-eun","\u00c9milie Bisaillon","Majd Al-Attiyah","Leila Hassan","Pedro Ramos","Leo Jackson","Haru Suzuki","Galina Morozova","Byung Goo Kim","William Edwards","Julie-Anne Lepage","Fatima Hamza","Violeta Vazquez","Julia Becker","Hind Al-Naimi","Yui Kimura","Andr\u00e9s Castro","Lee Yoo-bi","Luc\u00eda D\u00edaz","Jeremiah Parker","Lee Chae-won","Hessa Al-Muzaini","Moon Ji-hye","Oh Sung-min","Hiroaki Saito","Fernanda Velasco","Samar Al-Sabah","Lucie Bertrand","Annika Haas","Chlo\u00e9 Lefebvre","Jung Yong Hwa","Hossam Helmy","Kim Jong-kook","Sara Moulin","Nikolay Smirnov","Eli Bryant","Vivian Brown","Jawad Al-Najjar","Kosuke Sato","Vanessa Lemaire","Waled Al-Qahtani","Gregory Masson","Thomas Mary","Yoshinori Suzuki","Carina Hofmann","Ang\u00e9lique Paquin","Ivan Popov","Ekaterina Fedorova","Natalia Kuznetsova","Alba Garcia","Mariya Kuzmina","Lee Sun-kyun","R\u00e9mi Gagnon","Vladimir Petrov","Nozomi Sasaki","Valentin Fernandez","Saori Nakajima","David Nguyen","Kaede Nakamura","Nahla Hamdi","Juan Martinez","Zara Garcia","Lena Hussain","Thomas Petit","Olivier Samson","Sanaa Al-Mesned","Mitsuko Nakajima","Marta Vargas","Iyad Al-Din","Martin Perrault","Yuji Takahashi","Im Chang-jung","Zoey Coleman","Kaori Tanaka","Celine Dubois","Jang Na-ra","Park Yoo-ri","Destiny Phillips","Zachary Martinez","Hyo Jin Kim","Nanami Nakamura","Brady Chen","Elena Smirnova","Yui Nakamura","Omar Mohamed","Anabel Vogt","Jason Harris","Juli\u00e1n S\u00e1nchez","Akihiro Nakamura","Kuzmicheva Ivanova","Ivanov Gavrilov","Mayumi Nakamura","Am\u00e9lie Gagnon","Viacheslav Ivanov","Eva Watson","Vanessa Mayer","Lena Kim","Eva Aguilar","Matthias Weber","Marius Franz","Irina Volkova","Marwa Al-Jabir","Mar\u00eda Gonz\u00e1lez","Nadia Al-Shami","Yves Tremblay","Ji Yoon Lee","Daniel Ramos","Park Geun Hye","Louna Coulon","Nathalie Gauthier","Jumana Al-Qubaisi","Basma Ali","Miki Tanaka","Lukas Sauer","Gabriel Martin","J\u00e9r\u00f4me Pelletier","Daniel Valdez","Alina Frank","Yuka Yamamoto","Sebastian Huber","M\u00f3nica Ram\u00edrez","Fusako Suzuki","Jaber Al-Mutawa","Sung Ho Kim","Louise L\u00e9vesque","Kazuyuki Aoki","Guseva Anastasia","Kwon Kyung-soo","Sanaa Salah","Claire Hayes","Jessica Werner","Lara Schneider","Oksana Kuznetsova","Hye Won Park","Tobias Klein","Kazuki Takahashi","Matthew Johnson","Achim Krause","Carolin Sch\u00e4fer","Franck Fabre","Sylvain Francois","Tobias Bauer","Helene Bernard","Laura Caron","Valentin Smirnov","Morgan Smith","Khalifa Al-Kuwari","Avery Singh","Alexander Volkov","Lola Robin","Daniel Gagn\u00e9","Zakiya Saleh","Sergei Prokofiev","Diane Roy","Song Ji-hyo","Mubarak Al-Fahim","Safiya Omar","Takako Nakamura","Adri\u00e1n D\u00edaz","Joshua Lopez","Satoshi Nakamura","Hitomi Yamamoto","Maria Berger","Shinichi Nakamura","Tariq Al-Ibrahim","Kim Min-joon","Tom Schmidt","Pedro Ortiz","Lucia Medina","Manuel Perez","In\u00e8s Giraud","Liam Johnson","Sophie Tremblay","Simon Fischer","Owen Murphy","Hirofumi Suzuki","Emily Patel","Hendrik Baumann","Amelia White","Omar Al-Ansari","Silvia Alonso","Yosuke Ito","Piper Gonzalez","Khalil Ibrahim","Nobuyuki Ishikawa","Joseph Ortiz","Manon Poirier","Anna Ivanova","Estelle Archambault","Jocelyn Hernandez","Florian Baum","Laura Cruz","Dominic Keller","Michel Dub\u00e9","Fumiko Yamamoto","Olga Kuznetsova","Victoria Heinrich","Shunsuke Nakayama","Min Ju Choi","Yui Kawamoto","Andreas Keller","Vanesa Alonso","Ahmed Al-Masri","Elisa Petit","Sayaka Morimoto","Saeed Al-Kaabi","Oliver Fuchs","Won Ho Lee","Eri Nakamura","David Gagnon","Mar\u00eda Jos\u00e9 Mart\u00ednez","Enrique Ruiz","Lorena Molina","Mar\u00eda \u00c1ngeles L\u00f3pez","Ana Martinez","Charlotte Dupuis","Hassan Al-Basha","Jessie Johnson","Fanny Laroche","Lilia Ivanova","Marie-Andr\u00e9e Leclerc","Lee Jin-wook","Francisco Moreno","Kim Sung-joon","John Ross","Katrin Sommer","Carlos Garc\u00eda","Anastasia Kuznetsova","Gabriel Martinez","Jasmine Scott","Alexander Kozlov","Manon Renault","Jos\u00e9 Antonio Ramos","Valentina Ortiz","Tae Hyun Park","Sarah Hermann","David Rodr\u00edguez","Svetlana Mikhailova","Tomas Rodriguez","Hassan Al-Marri","Asuka Nakagawa","Saskia Koch","David Morissette","Veronika Petrova","Mayu Ikeuchi","Mathieu Dupuy","Alexandr Ivanov","Moon Ji-hyun","Janina Richter","Romane David","Miho Saito","Camila Martinez","Kim Hyun Joong","Takumi Suzuki","Keiko Nakagawa","Walid Hamza","Wyatt Rogers","Dylan Scott","Tyler Parker","Lara Schumacher","Nancy Thibodeau","Elena Castro","Jonas Mayer","Svetlana Ivanova","Tatiana Ivanova","Gabriella Turner","Lauren Morris","Marina Orlova","Olga Morozova","Carolin Schumacher","Mohamed Said","Am\u00e9lie Fischer","Dominic Schmidt","Koichi Abe","Laura Garc\u00eda","Margarita Bogdanova","Hind Al-Mutairi","Tatiana Petrova","Arisa Yamamoto","Lubna Al-Sharif","Salam Al-Hamad","Delphine Maurice","David Ouellet","Tariq Elsayed","Misa Matsumoto","Ralf Kr\u00fcger","Yuto Tanaka","Rima Al-Saadi","Lucas K\u00f6hler","Maryam Al-Zahrani","Valentina Ivanova","Vladimir Karpov","Mark Tremblay","Yana Ivanova","Lim Yeon-woo","Rasha Salem","Denis Kuznetsov","Raquel Garcia","Aria Parker","Sara Al-Sada","Emilie Lefevre","Sarah Schmid","Nikolai Ivanov","Johannes Herzog","Gustavo Perez","Jos\u00e9 Rodr\u00edguez","Kiera Nguyen","Hind Al-Kandari","Francisco Gonz\u00e1lez","Sergei Kozlov","Moritz Frank","Saki Ito","Paula Zimmermann","Carina Keller","Alia Mahmoud","Katsuya Ito","Seung Hoon Park","Yasmine Schmitt","Danil Ivanov","Javier Ruiz","Andres Gomez","Sarah Becker","Octavio Soto","Akane Nakajima","Sabrina Ritter","Lillian White","Tomoko Nakamura","Gisela Rios","Nathalie Martin","Zoya Kuznetsova","Fawzia Al-Oufi","Hala Al-Qahtani","Tristan Peterson","Nicholas Baker","Mireille Simon","Maymunah Al-Atiya","Sara Neumann","Hinata Nakamura","Charlotte Martinez","Adnan Al-Muhanna","Katherine Patterson","Nina Morozov","Javier Ramos","Tina M\u00fcller","Danil Petrov","Ethan Wu","Gavin Lee","Mathieu B\u00e9langer","Emily Martinez","Alix Lambert","Julia Schneider","Susumu Yamamoto","Oksana Ivanova","Khalid Alawi","Naoko Ishikawa","Shin Ji-eun","Ali Al-Sheikh","Valentina R\u00edos","Talal Al-Jufairi","Polina Ivanova","Jang Geun-suk","\u00c9lise Deschamps","Diego Alvarez","Jang Tae-joon","Yaroslav Kuznetsov","Juan Carlos Martinez","Gloria Castro","Jeon Mi-do","Rafael Gil","Sang Joon Park","Satoko Nakamura","Abdul Rahman Hamid","Zoe Braun","Bae Doo Na","Han Joon-hee","Phillip Peters","Kenichi Inoue","Rami Saad","Choi Eun-hee","Christian Bauer","Asuka Takahashi","\u00c9tienne Bergeron","Leo Lee","Mara Hermann","Ryota Suzuki","Jonathan Coutu","Rina Yamamoto","Jorge Moore","Francisco Romero","Monica Aguilar","Zoe Martin","Koichi Nakajima","Quirin Bergmann","Isaac Rodriguez","Adham El-Gamal","Jose Ramirez","Jesus Leon","Joseph Barnes","Olga Smirnova","Ashley White","Kim Ji-won","Julian Grau","Ryoichi Ito","Han Dong-wook","Julie Lapointe","Frank Winkler","Elena Kuznetsova","Hussein Al-Qasimi","Valentin Chevalier","Francois Leblanc","Gong Yoo","Guillaume Dupont","Maher Al-Badawi","Oleg Petrov","Lucas Baker","Janine R\u00f6der","Makoto Fujimoto","Nikolay Ivanov","Lena Schr\u00f6der","Alexis Brun","Yevgeny Volkov","Mai Yamazaki","Melissa King","Grecia Hern\u00e1ndez","S\u00e9bastien Bouchard","Amanda Navarro","Ruben Jim\u00e9nez","Nicholas Scott","Ryan Lewis","\u00c9milie Girard","Elena Ruiz","Maria Sokolova","Lauren Davis","Tatiana Voronina","Yoshimi Suzuki","Arisa Yamashita","Yoon Jeong-hoon","Lee Dong Wook","Aaron Phillips","Caroline L\u00e9vesque","Lucie Arnaud","Natalia Mu\u00f1oz","Victoire Chauveau","Jackson Wilson","Raven Williams","Marco Wolf","Aya Nakano","Gianna Rowe","Svetlana Ivanova Jr.","Sophia Hess","Sabine Wagner","Benjamin Schumacher","Sergei Anisimov","Mercedes Gallego","Sophia Wimmer","Anke Keller","In\u00e8s Fran\u00e7ois","Yui Suzuki","Bennet Schmitt","Marina Ivanova","Ma\u00eblle Perrot","Go Soo","Joshua Hernandez","Levi Scott","Khulood Al-Qahtani","Aaliyah King","Diana Petrova","Makayla Clark","Caroline Roger","Jos\u00e9 Garc\u00eda","Oscar Ward","Lee Byung-hun","Gabriela Flores","Yoon Soo Ah","Louis-Arthur Pelletier","Youssef Khalil","Ren Nakamura","Natalia Pascual","Rabia Al-Bashaar","Rafael Guzman","Ver\u00f3nica Guti\u00e9rrez","Charlotte Rousseau","Grace Campbell","Louis Philippe","Kim Young Min"];
    }

    /* ----------------------------------------------- 
        BASE64 FUNCTIONS
    -------------------------------------------------*/
    /**
     * Converts a string to base64 by first switching it to an UTF-8 encoding and then converting it to base64
     * @param {string} str  String to convert
     * @return string       Base64 encoded string
     */
    static base64encode(str)
    {
        let bytes = new TextEncoder("utf-8").encode(str);
        const binString = Array.from(bytes, (x) => String.fromCodePoint(x)).join("");
        return btoa(binString);
    }

    /**
     * Converts a base64 encoded string to a normal string and then converts it to UTF-8
     * @param {string} base64   Base64 encoded string
     * @return string           Decoded string
     */
    static base64decode(base64)
    {
        let binString = atob(base64);
        let bytes = Uint8Array.from(binString, (m) => m.codePointAt(0));

        return new TextDecoder().decode(bytes);
    }
}